import React, { useState, useEffect, useCallback, useMemo } from 'react';
import UnderwritingShareSection from '../shared/UnderwritingShareSection';
import ApplicationFieldsPanel from '../shared/ApplicationFieldsPanel';
import UseOfFundsScheduleCard from '../shared/UseOfFundsScheduleCard';
import BalanceSheetCard from '../shared/BalanceSheetCard';
import DocumentAnalysisPanel from './DocumentAnalysisPanel';
import JumboAdditionalOwners from './JumboAdditionalOwners';
import SBAEligibilityCard from '../toolkit/SBAEligibilityCard';
import { Button } from '../ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Label } from '../ui/label';
import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Alert, AlertDescription } from '../ui/alert';
import { StatusBadge } from '../shared/StatusBadge';
import DocumentUploader from '../shared/DocumentUploader';
import { productLabel, getForeclosureName } from './loanFieldConstants';
import {
  X, CheckCircle, XCircle, Clock, Eye, EyeOff, AlertCircle, Ban,
  FileText, Building2, User, DollarSign, UserCheck,
  FileSignature, Lock, Plus, Trash2, ExternalLink, Link, Save,
  StickyNote, Pencil, Send, Award, ThumbsUp, ThumbsDown, Bell, RefreshCw, MessageSquare, Mail,
  Share2, Copy, Check, Repeat2, Upload, Loader2, BarChart3, ChevronDown, ChevronRight, Wrench
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';
import { RequestInfoPanel } from './RequestInfoPanel';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const SSNToggle = ({ value }) => {
  const [visible, setVisible] = React.useState(false);
  if (!value) return <span className="text-slate-400">Not provided</span>;
  const str = String(value);
  const masked = str.length <= 4 ? '***-**-' + str : '***-**-' + str.slice(-4);
  return (
    <span className="inline-flex items-center gap-1.5">
      <span>{visible ? str : masked}</span>
      <button
        onClick={() => setVisible(!visible)}
        className="text-slate-400 hover:text-slate-600 transition-colors p-0.5 rounded"
        title={visible ? 'Hide SSN' : 'Show SSN'}
        data-testid="ssn-toggle"
      >
        {visible ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
      </button>
    </span>
  );
};

// All statuses available for admin/underwriter (can move to any status)
const ALL_STATUSES = [
  'incomplete',
  'pending_info_docs',
  'submitted',
  'submission_complete',
  'prequalified',
  'borrower_accepts_offer',
  'borrower_declines_offer',
  'pending_docs',
  'under_review',
  'in_document_review',
  'in_queue_for_underwriting',
  'in_initial_underwriting',
  'in_final_underwriting',
  'broker_attention_needed',
  'file_ready_for_underwriter',
  'underwriter_attention_needed',
  'approved',
  'closing',
  'closed',
  'funded',
  'declined',
  'withdrawn',
];

// Merchant Processing has its own pipeline. Underwriter Attention Needed
// and Broker Attention Needed are kept; the rest of the standard
// underwriting/closing pipeline does not apply.
const MP_ALL_STATUSES = [
  'incomplete',
  'submitted',                    // displayed as "Submitted for Quote"
  'mp_quote_issued',
  'mp_merchant_accepts_quote',
  'mp_please_collect_digital_mpa',
  'mp_ready_for_onboarding',
  'mp_onboarding',
  'mp_activated',
  'mp_account_approved',
  'mp_account_declined',
  'broker_attention_needed',
  'underwriter_attention_needed',
  'withdrawn',
];

// Brokers on Merchant Processing apps may set these statuses
const MP_BROKER_TRANSITIONS = {
  // From any state (where they have access), brokers can move forward
  mp_quote_issued: ['mp_merchant_accepts_quote', 'withdrawn'],
  mp_please_collect_digital_mpa: ['mp_ready_for_onboarding', 'withdrawn'],
  // Standard broker transitions still apply where relevant
  submitted: ['withdrawn'],
  broker_attention_needed: ['file_ready_for_underwriter', 'withdrawn'],
  underwriter_attention_needed: ['file_ready_for_underwriter', 'withdrawn'],
};

// Flow-based status transitions for brokers (restricted)
const BROKER_TRANSITIONS = {
  // From "Application Started — Pending Info & Docs":
  //   • submitted  → renders as "Complete Submission" button (the main action)
  //   • file_ready_for_underwriter → still available as a power-user shortcut
  //   • withdrawn  → broker can abandon the app
  pending_info_docs: ['submitted', 'file_ready_for_underwriter', 'withdrawn'],
  prequalified: ['borrower_accepts_offer', 'borrower_declines_offer', 'withdrawn'],
  pending_docs: ['file_ready_for_underwriter', 'withdrawn'],
  under_review: ['withdrawn'],
  submitted: ['withdrawn'],
  broker_attention_needed: ['file_ready_for_underwriter', 'withdrawn'],
  underwriter_attention_needed: ['file_ready_for_underwriter', 'withdrawn'],
};

const STATUS_BUTTON_CONFIG = {
  incomplete:              { icon: AlertCircle, color: 'border-amber-400 text-amber-700 hover:bg-amber-50', variant: 'outline' },
  pending_info_docs:       { icon: AlertCircle, color: 'border-amber-400 text-amber-800 hover:bg-amber-50 font-semibold', variant: 'outline' },
  submitted:               { icon: Send, color: 'bg-blue-600 hover:bg-blue-700 text-white' },
  submission_complete:     { icon: CheckCircle, color: 'bg-sky-600 hover:bg-sky-700 text-white' },
  prequalified:            { icon: Award, color: 'bg-indigo-600 hover:bg-indigo-700 text-white' },
  borrower_accepts_offer:  { icon: ThumbsUp, color: 'bg-lime-600 hover:bg-lime-700 text-white' },
  borrower_declines_offer: { icon: ThumbsDown, color: 'bg-rose-600 hover:bg-rose-700 text-white' },
  under_review:            { icon: Eye, color: 'bg-amber-600 hover:bg-amber-700 text-white' },
  pending_docs:            { icon: AlertCircle, color: 'border-orange-300 text-orange-700 hover:bg-orange-50', variant: 'outline' },
  approved:                { icon: CheckCircle, color: 'bg-emerald-600 hover:bg-emerald-700 text-white' },
  declined:                { icon: XCircle, color: 'bg-red-600 hover:bg-red-700 text-white' },
  closing:                 { icon: FileSignature, color: 'bg-violet-600 hover:bg-violet-700 text-white' },
  closed:                  { icon: Lock, color: 'bg-[#0E333F] hover:bg-[#081120] text-white' },
  funded:                  { icon: DollarSign, color: 'bg-teal-600 hover:bg-teal-700 text-white' },
  withdrawn:               { icon: Ban, color: 'bg-gray-600 hover:bg-gray-700 text-white' },
  broker_attention_needed:      { icon: AlertCircle, color: 'bg-red-600 hover:bg-red-700 text-white' },
  file_ready_for_underwriter:   { icon: CheckCircle, color: 'bg-purple-500 hover:bg-purple-600 text-white' },
  underwriter_attention_needed: { icon: AlertCircle, color: 'bg-red-600 hover:bg-red-700 text-white' },
  in_document_review:          { icon: Eye, color: 'bg-amber-500 hover:bg-amber-600 text-white' },
  in_queue_for_underwriting:   { icon: Clock, color: 'bg-yellow-500 hover:bg-yellow-600 text-white' },
  in_initial_underwriting:     { icon: Eye, color: 'bg-teal-500 hover:bg-teal-600 text-white' },
  in_final_underwriting:       { icon: Eye, color: 'bg-emerald-500 hover:bg-emerald-600 text-white' },
  // Merchant Processing-specific status buttons
  mp_quote_issued:               { icon: FileSignature, color: 'bg-cyan-600 hover:bg-cyan-700 text-white' },
  mp_merchant_accepts_quote:     { icon: ThumbsUp, color: 'bg-lime-600 hover:bg-lime-700 text-white' },
  mp_please_collect_digital_mpa: { icon: AlertCircle, color: 'bg-orange-600 hover:bg-orange-700 text-white' },
  mp_ready_for_onboarding:       { icon: CheckCircle, color: 'bg-violet-600 hover:bg-violet-700 text-white' },
  mp_onboarding:                 { icon: Eye, color: 'bg-indigo-600 hover:bg-indigo-700 text-white' },
  mp_activated:                  { icon: CheckCircle, color: 'bg-emerald-700 hover:bg-emerald-800 text-white' },
  mp_account_approved:           { icon: CheckCircle, color: 'bg-emerald-600 hover:bg-emerald-700 text-white' },
  mp_account_declined:           { icon: XCircle, color: 'bg-red-600 hover:bg-red-700 text-white' },
};

const STATUS_LABELS = {
  incomplete: 'Mark Incomplete',
  pending_info_docs: 'Pending Info & Docs',
  submitted_from_link: 'Submitted from Link',
  submitted: 'Submitted',
  submission_complete: 'Submission Complete',
  prequalified: 'Prequalified',
  borrower_accepts_offer: 'Borrower Accepts Offer',
  borrower_declines_offer: 'Borrower Declines Offer',
  under_review: 'Under Review',
  pending_docs: 'Pending Docs/Info',
  approved: 'Approve',
  declined: 'Decline',
  closing: 'In Closing',
  closed: 'Closed',
  funded: 'Funded',
  withdrawn: 'Withdrawn',
  broker_attention_needed: 'Broker Attention Needed',
  file_ready_for_underwriter: 'File Ready for UW',
  underwriter_attention_needed: 'UW Attention Needed',
  in_document_review: 'In Document Review',
  in_queue_for_underwriting: 'In Queue for Underwriting',
  in_initial_underwriting: 'In Initial Underwriting',
  in_final_underwriting: 'In Final Underwriting',
  // Merchant Processing-specific labels
  mp_quote_issued: 'Quote Issued',
  mp_merchant_accepts_quote: 'Merchant Accepts Quote',
  mp_please_collect_digital_mpa: 'Please Collect Digital MPA',
  mp_ready_for_onboarding: 'Ready for Onboarding',
  mp_onboarding: 'Onboarding',
  mp_activated: 'Activated',
  mp_account_approved: 'Account Approved',
  mp_account_declined: 'Account Declined',
};

const ApprovalTermsEditor = ({ application, canEdit, label = 'Approval Terms' }) => {
  const [terms, setTerms] = useState(application.approval_terms || '');
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(false);
  const hasTerms = !!application.approval_terms;

  if (!canEdit && !hasTerms) return null;

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${BACKEND_URL}/api/applications/${application.id}/approval-terms`, { approval_terms: terms });
      toast.success('Approval terms saved');
      setEditing(false);
    } catch (err) {
      toast.error('Failed to save approval terms');
    } finally {
      setSaving(false);
    }
  };

  // Read-only view for brokers or when not editing
  if (!canEdit || (!editing && hasTerms)) {
    return (
      <Card className="border-emerald-200 bg-emerald-50" data-testid="approval-terms-banner">
        <CardContent className="py-4">
          <div className="flex items-start gap-3">
            <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <p className="text-sm font-semibold text-emerald-800">{label}</p>
                {canEdit && (
                  <Button variant="ghost" size="sm" onClick={() => setEditing(true)} className="h-7 px-2 text-emerald-700 hover:text-emerald-900" data-testid="edit-approval-terms-btn">
                    <Pencil className="h-3.5 w-3.5 mr-1" /> Edit
                  </Button>
                )}
              </div>
              <p className="text-sm text-emerald-700 mt-1 whitespace-pre-line">{application.approval_terms}</p>
              {application.approved_by && (
                <p className="text-xs text-emerald-500 mt-2">
                  — {application.approved_by}
                  {application.approved_at && `, ${new Date(application.approved_at).toLocaleDateString()}`}
                </p>
              )}
            </div>
          </div>
        </CardContent>
      </Card>
    );
  }

  // Editable view for underwriter/admin
  return (
    <Card className="border-emerald-200 bg-emerald-50" data-testid="approval-terms-editor">
      <CardContent className="py-4">
        <div className="flex items-start gap-3">
          <CheckCircle className="h-5 w-5 text-emerald-600 mt-0.5 shrink-0" />
          <div className="flex-1 space-y-2">
            <p className="text-sm font-semibold text-emerald-800">{label}</p>
            <textarea
              value={terms}
              onChange={(e) => setTerms(e.target.value)}
              placeholder={`Enter terms: amount, rate, term length, conditions...`}
              className="w-full min-h-[80px] p-2 text-sm text-slate-900 placeholder:text-slate-400 border border-emerald-300 rounded-md bg-white focus:outline-none focus:ring-2 focus:ring-emerald-400 resize-y"
              data-testid="approval-terms-input"
            />
            <div className="flex gap-2">
              <Button size="sm" onClick={handleSave} disabled={saving || !terms.trim()} className="bg-emerald-600 hover:bg-emerald-700 text-white h-8" data-testid="save-approval-terms-btn">
                <Save className="h-3.5 w-3.5 mr-1" /> {saving ? 'Saving...' : 'Save Terms'}
              </Button>
              {hasTerms && (
                <Button variant="ghost" size="sm" onClick={() => { setTerms(application.approval_terms || ''); setEditing(false); }} className="h-8 text-slate-600">
                  Cancel
                </Button>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

const ApplicationReview = ({ application, onClose, onStatusUpdate, onOpenMessages }) => {
  const { selectedRole, user, token } = useAuth();
  const [updating, setUpdating] = useState(false);
  const [brokers, setBrokers] = useState([]);
  const [borrowers, setBorrowers] = useState([]);
  const [selectedBroker, setSelectedBroker] = useState(application?.broker_id || '');
  const [selectedBorrower, setSelectedBorrower] = useState(application?.borrower_id || '');
  const [missingDocs, setMissingDocs] = useState(application?.missing_documents || []);
  const [newDoc, setNewDoc] = useState('');
  const [savingDocs, setSavingDocs] = useState(false);
  const [docLink, setDocLink] = useState(application?.document_link || '');

  // Normalize URL — ensure it has a scheme so href doesn't treat it as a
  // relative path. Mirrored in dashboards; keep in sync if changed.
  const absoluteUrl = (raw) => {
    const v = String(raw || '').trim();
    if (!v) return '';
    if (/^(https?:\/\/|mailto:|tel:)/i.test(v)) return v;
    // strip any leading "//" to avoid protocol-relative weirdness
    return `https://${v.replace(/^\/+/, '')}`;
  };
  const [savingLink, setSavingLink] = useState(false);
  const [notes, setNotes] = useState([]);
  const [newNote, setNewNote] = useState('');
  const [savingNote, setSavingNote] = useState(false);
  const [editingNoteId, setEditingNoteId] = useState(null);
  const [editNoteContent, setEditNoteContent] = useState('');
  const [notifying, setNotifying] = useState(false);
  const [liveRequiredDocs, setLiveRequiredDocs] = useState(application?.required_documents || []);
  const [refreshingDocs, setRefreshingDocs] = useState(false);
  const [liveDebtSchedule, setLiveDebtSchedule] = useState(application?.debt_schedule || []);
  const [liveDebtPdfUrl, setLiveDebtPdfUrl] = useState(application?.debt_schedule_pdf_url || null);
  const [livePfsData, setLivePfsData] = useState(application?.personal_financial_statements || application?.personal_financial_statement || {});
  const [livePfsPdfUrl, setLivePfsPdfUrl] = useState(application?.pfs_pdf_url || null);
  const [liveApp, setLiveApp] = useState(application);
  const [reminderModalOpen, setReminderModalOpen] = useState(false);
  const [reminderNote, setReminderNote] = useState('');
  const [reminderWhen, setReminderWhen] = useState('');
  const [savingReminder, setSavingReminder] = useState(false);

  const isAdmin = selectedRole === 'admin';
  const isUnderwriter = selectedRole === 'underwriter';
  const isJrUnderwriter = selectedRole === 'jr_underwriter';
  const isBroker = selectedRole === 'broker';
  // Jr Underwriters have nearly the same access as underwriters (can manage docs, set link, etc.)
  const canDeleteFiles = isAdmin || isUnderwriter || isBroker; // Jr UW explicitly excluded
  const canManageDocs = isAdmin || isUnderwriter || isJrUnderwriter;
  const canSetLink = isAdmin || isUnderwriter || isJrUnderwriter || isBroker;
  // Effective underwriter access for UI gates (notes, terms preview, etc.) — but NOT for
  // marking approved/declined which is gated below.
  const hasUWAccess = isAdmin || isUnderwriter || isJrUnderwriter;

  useEffect(() => {
    fetchBrokers();
    fetchBorrowers();
    fetchNotes();
    refreshApplicationDocs();
  }, []);

  const refreshApplicationDocs = useCallback(async () => {
    setRefreshingDocs(true);
    try {
      const response = await axios.get(`${BACKEND_URL}/api/applications/${application.id}`);
      const data = response.data;
      setLiveRequiredDocs(data.required_documents || []);
      setLiveDebtSchedule(data.debt_schedule || []);
      setLiveDebtPdfUrl(data.debt_schedule_pdf_url || null);
      setLivePfsData(data.personal_financial_statements || data.personal_financial_statement || {});
      setLivePfsPdfUrl(data.pfs_pdf_url || null);
      setLiveApp(data);
    } catch (error) {
      console.error('Error refreshing docs:', error);
    } finally {
      setRefreshingDocs(false);
    }
  }, [application.id]);

  const fetchBrokers = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/brokers`);
      setBrokers(response.data);
    } catch (error) {
      console.error('Error fetching brokers:', error);
    }
  };

  const fetchBorrowers = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/borrowers`);
      setBorrowers(response.data);
    } catch (error) {
      console.error('Error fetching borrowers:', error);
    }
  };

  const fetchNotes = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/notes/application/${application.id}`);
      setNotes(response.data.notes || []);
    } catch (error) {
      console.error('Error fetching notes:', error);
    }
  };

  const handleCreateNote = async () => {
    if (!newNote.trim()) return;
    setSavingNote(true);
    try {
      const response = await axios.post(`${BACKEND_URL}/api/notes`, {
        application_id: application.id,
        content: newNote.trim()
      });
      setNotes(prev => [response.data, ...prev]);
      setNewNote('');
    } catch (error) {
      console.error('Error creating note:', error);
    } finally {
      setSavingNote(false);
    }
  };

  const handleUpdateNote = async (noteId) => {
    if (!editNoteContent.trim()) return;
    try {
      await axios.put(`${BACKEND_URL}/api/notes/${noteId}`, { content: editNoteContent.trim() });
      setNotes(prev => prev.map(n => n.id === noteId ? { ...n, content: editNoteContent.trim(), updated_at: new Date().toISOString() } : n));
      setEditingNoteId(null);
      setEditNoteContent('');
    } catch (error) {
      console.error('Error updating note:', error);
    }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/notes/${noteId}`);
      setNotes(prev => prev.filter(n => n.id !== noteId));
    } catch (error) {
      console.error('Error deleting note:', error);
    }
  };

  const handleNotifyDocsUploaded = async () => {
    setNotifying(true);
    try {
      await axios.post(`${BACKEND_URL}/api/applications/${application.id}/notify-docs-uploaded`);
      alert('Underwriter has been notified that documents are uploaded.');
    } catch (error) {
      console.error('Error notifying:', error);
      alert('Failed to send notification.');
    } finally {
      setNotifying(false);
    }
  };

  const handleAssignBroker = async () => {
    if (!selectedBroker) return;
    setUpdating(true);
    try {
      await axios.put(`${BACKEND_URL}/api/applications/${application.id}/assign-broker?broker_id=${selectedBroker}`);
      onStatusUpdate(application.id, application.status);
    } catch (error) {
      console.error('Error assigning broker:', error);
    } finally {
      setUpdating(false);
    }
  };

  const handleAssignBorrower = async () => {
    if (!selectedBorrower) return;
    setUpdating(true);
    try {
      await axios.put(`${BACKEND_URL}/api/applications/${application.id}/assign-borrower?borrower_id=${selectedBorrower}`);
      onStatusUpdate(application.id, application.status);
    } catch (error) {
      console.error('Error assigning borrower:', error);
    } finally {
      setUpdating(false);
    }
  };

  if (!application) return null;

  const handleStatusUpdate = async (newStatus) => {
    let declineReason = '';
    let approvalTerms = '';
    if (newStatus === 'declined') {
      declineReason = window.prompt('Enter the reason for declining this application:');
      if (declineReason === null) return;
    }
    if (newStatus === 'borrower_declines_offer') {
      declineReason = window.prompt('Enter the reason the borrower is declining:');
      if (declineReason === null) return;
    }
    if (newStatus === 'withdrawn') {
      declineReason = window.prompt('Enter the reason for withdrawing this application:');
      if (declineReason === null) return;
    }
    if (newStatus === 'approved' || newStatus === 'prequalified') {
      const promptLabel = newStatus === 'prequalified' ? 'prequalification' : 'approval';
      approvalTerms = window.prompt(`Enter the ${promptLabel} terms (amount, rate, term, conditions):`);
      if (approvalTerms === null) return;
    }

    // Ask if they want to notify the broker (skip for incomplete — no notification)
    let notifyBroker = false;
    if (newStatus !== 'incomplete') {
      notifyBroker = window.confirm('Would you like to send a notification email to the broker about this status change?');
    }

    // For underwriters/admins setting "Broker Attention Needed", optionally schedule a reminder
    let openReminderAfter = false;
    if (newStatus === 'broker_attention_needed' && (isUnderwriter || isAdmin)) {
      openReminderAfter = window.confirm('Would you like to set a reminder to follow up with the broker?');
    }

    setUpdating(true);
    try {
      let url = `${BACKEND_URL}/api/applications/${application.id}/status?status=${newStatus}`;
      if (declineReason) {
        url += `&decline_reason=${encodeURIComponent(declineReason)}`;
      }
      if (approvalTerms) {
        url += `&approval_terms=${encodeURIComponent(approvalTerms)}`;
      }
      if (notifyBroker) {
        url += `&notify_broker=true`;
      }
      await axios.put(url);

      if (openReminderAfter) {
        // Defer onStatusUpdate / onClose until the reminder modal is dismissed
        // (otherwise the parent dashboard unmounts ApplicationReview and destroys this state)
        const dt = new Date();
        dt.setDate(dt.getDate() + 1);
        const pad = (n) => String(n).padStart(2, '0');
        const localDefault = `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
        setReminderNote('');
        setReminderWhen(localDefault);
        setReminderModalOpen(true);
      } else {
        onStatusUpdate(application.id, newStatus);
        onClose();
      }
    } catch (error) {
      console.error('Error updating status:', error);
    } finally {
      setUpdating(false);
    }
  };

  const submitReminder = async () => {
    if (!reminderWhen) {
      toast.error('Please choose a date and time for the reminder');
      return;
    }
    const scheduledIso = new Date(reminderWhen).toISOString();
    if (new Date(scheduledIso).getTime() <= Date.now()) {
      toast.error('Reminder time must be in the future');
      return;
    }
    setSavingReminder(true);
    try {
      await axios.post(`${BACKEND_URL}/api/reminders`, {
        application_id: application.id,
        note: reminderNote || '',
        scheduled_at: scheduledIso,
      });
      toast.success('Reminder scheduled — you will receive a task and email at the chosen time');
      setReminderModalOpen(false);
      onStatusUpdate(application.id, 'broker_attention_needed');
      onClose();
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Failed to schedule reminder');
    } finally {
      setSavingReminder(false);
    }
  };

  const cancelReminder = () => {
    setReminderModalOpen(false);
    onStatusUpdate(application.id, 'broker_attention_needed');
    onClose();
  };

  const addMissingDoc = () => {
    const doc = newDoc.trim();
    if (doc && !missingDocs.includes(doc)) {
      setMissingDocs(prev => [...prev, doc]);
      setNewDoc('');
    }
  };

  const removeMissingDoc = (index) => {
    setMissingDocs(prev => prev.filter((_, i) => i !== index));
  };

  const saveMissingDocs = async () => {
    setSavingDocs(true);
    try {
      await axios.put(`${BACKEND_URL}/api/applications/${application.id}/missing-documents`, {
        missing_documents: missingDocs
      });
    } catch (error) {
      console.error('Error saving missing docs:', error);
    } finally {
      setSavingDocs(false);
    }
  };

  const saveDocumentLink = async () => {
    setSavingLink(true);
    try {
      await axios.put(`${BACKEND_URL}/api/applications/${application.id}/document-link`, {
        document_link: docLink
      });
      application.document_link = docLink;
      toast.success('Document link saved successfully.');
    } catch (error) {
      console.error('Error saving document link:', error);
      toast.error('Failed to save document link.');
    } finally {
      setSavingLink(false);
    }
  };

  const getBusinessName = () => {
    const a = liveApp || application;
    return a.term_loan_data?.legal_corporate_name ||
           a.term_loan_data?.business_name ||
           a.term_loan_10yr_data?.legal_corporate_name ||
           a.term_loan_10yr_data?.business_name ||
           a.term_loc_hybrid_data?.legal_corporate_name ||
           a.term_loc_hybrid_data?.business_name ||
           a.term_loan_3_5yr_data?.legal_corporate_name ||
           a.term_loan_3_5yr_data?.business_name ||
           a.sba_loan_data?.business_legal_name ||
           a.sba_loan_data?.business_name ||
           a.line_of_credit_data?.business_name ||
           a.equipment_financing_data?.business_name ||
           a.merchant_processing_data?.company_name ||
           a.merchant_processing_data?.business_legal_name ||
           a.merchant_processing_data?.business_name ||
           a.jumbo_term_loan_data?.business_legal_name ||
           a.jumbo_term_loan_data?.business_dba_name ||
           a.real_estate_financing_data?.legal_name_of_business ||
           a.debt_consulting_data?.company_legal_name ||
           a.debt_consulting_data?.company_dba ||
           a.debt_consulting_data?.legal_business_name ||
           a.debt_consulting_data?.dba ||
           a.family_office_data?.legal_business_name ||
           a.family_office_data?.dba ||
           getForeclosureName(a) ||
           (a.transportation_factoring_data?.first_name && a.transportation_factoring_data?.last_name
             ? `${a.transportation_factoring_data.first_name} ${a.transportation_factoring_data.last_name}` : null) ||
           a.transportation_factoring_data?.first_name ||
           a.business_name ||
           a.applicant_name ||
           'Business Application';
  };

  const getLoanAmount = () => {
    return application.term_loan_data?.funding_amount_requested ||
           application.term_loan_10yr_data?.funding_amount_requested ||
           application.term_loc_hybrid_data?.funding_amount_requested ||
           application.term_loc_hybrid_data?.total_loan_amount ||
           application.term_loan_3_5yr_data?.funding_amount_requested ||
           application.sba_loan_data?.loan_requested_amount ||
           application.line_of_credit_data?.requested_line_of_credit_amount ||
           application.equipment_financing_data?.amount_requested ||
           application.merchant_processing_data?.amount_requested ||
           application.jumbo_term_loan_data?.amount_requested ||
           application.debt_consulting_data?.amount_of_debt_to_refinance ||
           application.debt_consulting_data?.requested_funding_amount ||
           application.family_office_data?.requested_funding_amount ||
           application.foreclosure_bailout_data?.loan_amount ||
           application.real_estate_financing_data?.financing_amount_requested || 0;
  };

  const getBusinessAddress = () => {
    return application.term_loan_data?.business_address ||
           application.term_loan_10yr_data?.business_address ||
           application.term_loc_hybrid_data?.business_address ||
           application.term_loan_3_5yr_data?.business_address ||
           application.sba_loan_data?.business_address ||
           application.line_of_credit_data?.business_address ||
           application.equipment_financing_data?.business_address ||
           'Address not provided';
  };

  const getPrimaryContact = () => {
    const tld = application.term_loan_data || application.term_loan_10yr_data || application.term_loc_hybrid_data || application.term_loan_3_5yr_data;
    if (tld) {
      return {
        name: `${tld.first_name || ''} ${tld.last_name || ''}`.trim(),
        email: tld.email_address,
        phone: tld.phone_number,
        ownership: tld.ownership_percentage,
        dob: tld.date_of_birth,
        ssn: tld.social_security_number || tld.ssn_itin,
        address: tld.home_address,
      };
    } else if (application.sba_loan_data) {
      const sba = application.sba_loan_data;
      return {
        name: `${sba.first_name || ''} ${sba.last_name || ''}`.trim() || sba.first_last_name,
        email: sba.email_address,
        phone: sba.mobile_phone_number,
        ownership: sba.ownership_percentage,
        dob: sba.date_of_birth,
        ssn: sba.social_security_number,
        address: sba.home_address,
      };
    } else if (application.line_of_credit_data) {
      const loc = application.line_of_credit_data;
      return {
        name: `${loc.owner_first_name || ''} ${loc.owner_last_name || ''}`.trim() || loc.owner_name,
        email: loc.owner_email,
        phone: loc.owner_phone,
      };
    } else if (application.equipment_financing_data) {
      const eq = application.equipment_financing_data;
      return {
        name: `${eq.owner_first_name || ''} ${eq.owner_last_name || ''}`.trim() || eq.owner_name,
        email: eq.owner_email,
        phone: eq.owner_phone,
      };
    } else if (application.foreclosure_bailout_data) {
      const fb = application.foreclosure_bailout_data;
      return {
        name: `${fb.borrower_first_name || ''} ${fb.borrower_last_name || ''}`.trim(),
        email: fb.borrower_email,
        phone: fb.borrower_phone,
      };
    }
    return null;
  };

  const getAdditionalOwners = () => {
    return (application.sba_loan_data?.additional_owners || application.term_loan_data?.additional_owners || application.term_loan_10yr_data?.additional_owners || application.term_loc_hybrid_data?.additional_owners || application.term_loan_3_5yr_data?.additional_owners || []);
  };

  const contact = getPrimaryContact();
  const docsChanged = JSON.stringify(missingDocs) !== JSON.stringify(application?.missing_documents || []);
  const linkChanged = docLink !== (application?.document_link || '');

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg max-w-6xl w-full max-h-[90vh] overflow-y-auto chunky-scrollbar">
        <div className="flex items-center justify-between p-6 border-b">
          <div>
            <h2 className="text-xl font-semibold text-slate-800">{isBroker ? 'Application Details' : 'Application Review'}</h2>
            <p className="text-slate-600">{getBusinessName()}</p>
          </div>
          <div className="flex items-center gap-2">
            {onOpenMessages && (
              <button
                onClick={() => { onClose(); onOpenMessages(application.id); }}
                className="h-9 px-3 flex items-center gap-1.5 rounded-lg border-2 border-[#0E333F] text-[#0E333F] hover:bg-[#0E333F] hover:text-white text-sm font-semibold transition-all"
                data-testid="review-open-messages"
              >
                <MessageSquare className="h-4 w-4" />
                Messages
              </button>
            )}
            <button onClick={onClose} disabled={updating} className="h-9 w-9 flex items-center justify-center rounded-lg border-2 border-slate-300 bg-slate-100 text-slate-600 hover:bg-red-50 hover:border-red-400 hover:text-red-600 transition-all duration-200" data-testid="close-app-review">
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Latest AI Toolkit Analysis — featured at the very top so the
                underwriter sees EBITDA, financials and eligibility verdicts
                immediately. Auto-expanded (no click required). */}
            {(isAdmin || isUnderwriter || isJrUnderwriter || isBroker) && (
              <div className="lg:col-span-3">
                <ToolkitResultsSection
                  applicationId={application.id}
                  token={token}
                  canRefreshGuidelines={isAdmin || isUnderwriter || isJrUnderwriter}
                />
              </div>
            )}

            {/* Application Overview */}
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <FileText className="h-5 w-5" />
                  Application Overview
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center gap-4">
                  <StatusBadge status={application.status} productType={application.product_type} />
                  <Badge variant="outline">{productLabel(application.product_type).toUpperCase()}</Badge>
                  {application.application_group_id && (
                    <Badge variant="outline" className="text-violet-700 border-violet-200 bg-violet-50">Multi-Product</Badge>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <Label className="text-slate-600">Requested Amount</Label>
                    <p className="text-2xl font-bold text-green-600">${getLoanAmount().toLocaleString()}</p>
                  </div>
                  <div>
                    <Label className="text-slate-600">Application Date</Label>
                    <p className="font-semibold">{new Date(application.created_at).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <Label className="text-slate-600">Last Updated</Label>
                    <p className="font-semibold">{new Date(application.updated_at).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <Label className="text-slate-600">Application ID</Label>
                    <p className="font-mono text-sm">{application.id}</p>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Decline Reason Banner - visible to all when declined */}
            {application.status === 'declined' && application.decline_reason && (
              <Card className="border-red-200 bg-red-50" data-testid="decline-reason-banner">
                <CardContent className="py-4">
                  <div className="flex items-start gap-3">
                    <XCircle className="h-5 w-5 text-red-500 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-red-800">Decline Reason</p>
                      <p className="text-sm text-red-700 mt-1 whitespace-pre-line">{application.decline_reason}</p>
                      {application.declined_by && (
                        <p className="text-xs text-red-400 mt-2">
                          — {application.declined_by}
                          {application.declined_at && `, ${new Date(application.declined_at).toLocaleDateString()}`}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Borrower Decline Reason Banner */}
            {application.status === 'borrower_declines_offer' && application.borrower_decline_reason && (
              <Card className="border-red-200 bg-red-50" data-testid="borrower-decline-reason-banner">
                <CardContent className="py-4">
                  <div className="flex items-start gap-3">
                    <XCircle className="h-5 w-5 text-red-500 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-red-800">Borrower Decline Reason</p>
                      <p className="text-sm text-red-700 mt-1 whitespace-pre-line">{application.borrower_decline_reason}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Withdrawn Reason Banner */}
            {application.status === 'withdrawn' && application.withdraw_reason && (
              <Card className="border-gray-300 bg-gray-100" data-testid="withdraw-reason-banner">
                <CardContent className="py-4">
                  <div className="flex items-start gap-3">
                    <XCircle className="h-5 w-5 text-gray-600 mt-0.5 shrink-0" />
                    <div>
                      <p className="text-sm font-semibold text-gray-800">Withdrawal Reason</p>
                      <p className="text-sm text-gray-700 mt-1 whitespace-pre-line">{application.withdraw_reason}</p>
                      {application.withdrawn_by && (
                        <p className="text-xs text-gray-500 mt-2">
                          — {application.withdrawn_by}
                          {application.withdrawn_at && `, ${new Date(application.withdrawn_at).toLocaleDateString()}`}
                        </p>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Approval Terms — editable for underwriter/admin, read-only for jr_underwriter/brokers */}
            {(isAdmin || isUnderwriter || isJrUnderwriter || (application.approval_terms)) && (
              <ApprovalTermsEditor
                application={application}
                canEdit={isAdmin || isUnderwriter}
                label={application.status === 'prequalified' ? 'Prequalification Terms' : 'Approval Terms'}
              />
            )}

            {/* Status Actions - admins/underwriters/jr-underwriters see all, brokers see flow-based.
                Jr Underwriters cannot mark Approved or Declined (escalates to senior). */}
            {(() => {
              const currentStatus = application.status;
              const isMP = application.product_type === 'merchant_processing';
              let availableStatuses;
              if (isAdmin || isUnderwriter || isJrUnderwriter) {
                const universe = isMP ? MP_ALL_STATUSES : ALL_STATUSES;
                availableStatuses = universe.filter(s => s !== currentStatus);
                if (isJrUnderwriter) {
                  // Hide approved/declined terminal actions for Jr UW
                  availableStatuses = availableStatuses.filter(s => s !== 'approved' && s !== 'declined');
                }
              } else {
                const transitions = isMP ? MP_BROKER_TRANSITIONS : BROKER_TRANSITIONS;
                availableStatuses = transitions[currentStatus] || [];
              }

              if (availableStatuses.length === 0) return null;

              return (
                <Card>
                  <CardHeader>
                    <CardTitle className="flex items-center gap-2">
                      <CheckCircle className="h-5 w-5" />
                      Update Status
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {isJrUnderwriter && (
                      <div className="text-xs text-fuchsia-700 bg-fuchsia-50 border border-fuchsia-200 rounded px-3 py-2 mb-3" data-testid="jr-uw-status-notice">
                        Jr Underwriters cannot mark applications <span className="font-semibold">Approved</span> or <span className="font-semibold">Declined</span>. Escalate to your senior underwriter.
                      </div>
                    )}
                    <div className="space-y-2">
                      {availableStatuses.map((statusValue) => {
                        const cfg = STATUS_BUTTON_CONFIG[statusValue] || {};
                        const Icon = cfg.icon || Clock;
                        // Contextual override: the submit-to-underwriter button
                        // FROM "Pending Info & Docs" is the user-facing
                        // "Complete Submission" CTA and gets emerald styling so
                        // it visually stands out as the main action.
                        const isCompleteSubmission = currentStatus === 'pending_info_docs' && statusValue === 'submitted';
                        const label = isCompleteSubmission
                          ? 'Complete Submission'
                          : (STATUS_LABELS[statusValue] || statusValue);
                        const btnClass = isCompleteSubmission
                          ? 'bg-emerald-600 hover:bg-emerald-700 text-white'
                          : (cfg.color || '');
                        const ButtonIcon = isCompleteSubmission ? CheckCircle : Icon;
                        return (
                          <Button
                            key={statusValue}
                            size="sm"
                            variant={isCompleteSubmission ? 'default' : (cfg.variant || 'default')}
                            className={`w-full ${btnClass}`}
                            onClick={() => handleStatusUpdate(statusValue)}
                            disabled={updating}
                            data-testid={isCompleteSubmission ? 'status-btn-complete-submission' : `status-btn-${statusValue}`}
                          >
                            <ButtonIcon className="h-4 w-4 mr-2" />
                            {label}
                          </Button>
                        );
                      })}
                    </div>
                  </CardContent>
                </Card>
              );
            })()}

            {/* Document Link */}

            {/* Offer Documents - Underwriter/Admin/Jr UW upload section */}
            {(isAdmin || isUnderwriter || isJrUnderwriter) && (
              <OfferDocumentsSection applicationId={application.id} token={token} existingFiles={application.offer_documents || []} onUpdate={() => onStatusUpdate(application.id, application.status)} />
            )}

            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Link className="h-5 w-5" />
                  Document Link
                  {application.document_link && (
                    <a href={absoluteUrl(application.document_link)} target="_blank" rel="noopener noreferrer" className="ml-auto text-sm text-blue-600 hover:text-blue-800 flex items-center gap-1" data-testid="document-link-open">
                      <ExternalLink className="h-3.5 w-3.5" /> Open
                    </a>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-slate-500 mb-3">Provide a link to where documents are stored (Google Drive, Dropbox, etc.)</p>
                {canSetLink ? (
                  <>
                  <div className="flex items-center gap-2">
                    <Input
                      value={docLink}
                      onChange={(e) => setDocLink(e.target.value)}
                      placeholder="https://drive.google.com/folder/..."
                      className="flex-1"
                      data-testid="document-link-input"
                    />
                    <Button
                      size="sm"
                      onClick={saveDocumentLink}
                      disabled={savingLink || !linkChanged}
                      className="bg-[#0E333F] hover:bg-[#081120]"
                      data-testid="save-document-link-btn"
                    >
                      <Save className="h-4 w-4 mr-1" />
                      {savingLink ? 'Saving...' : 'Save'}
                    </Button>
                  </div>
                  {isBroker && (application.document_link || docLink) && (
                    <Button
                      size="sm"
                      onClick={handleNotifyDocsUploaded}
                      disabled={notifying}
                      className="mt-2 bg-blue-600 hover:bg-blue-700 text-white w-full"
                      data-testid="notify-docs-uploaded-btn"
                    >
                      <Bell className="h-4 w-4 mr-1" />
                      {notifying ? 'Notifying...' : 'Notify Underwriter — Documents Uploaded'}
                    </Button>
                  )}
                  </>
                ) : (
                  <p className="text-sm">
                    {application.document_link ? (
                      <a href={application.document_link} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline flex items-center gap-1">
                        <ExternalLink className="h-3.5 w-3.5" /> {application.document_link}
                      </a>
                    ) : (
                      <span className="text-slate-400">No document link provided yet</span>
                    )}
                  </p>
                )}
              </CardContent>
            </Card>

            {/* Use of Funds Schedule — every product with a detailed_use_of_funds field */}
            {application.product_type !== 'merchant_processing' && (
              <Card className="lg:col-span-3" data-testid="use-of-funds-section">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <DollarSign className="h-5 w-5 text-emerald-600" />
                    Use of Funds Schedule
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <UseOfFundsScheduleCard
                    applicationId={application.id}
                    items={(liveApp || application).use_of_funds_schedule || []}
                    authToken={localStorage.getItem('auth_token')}
                    amountRequested={getLoanAmount()}
                    onSave={() => refreshApplicationDocs()}
                  />
                </CardContent>
              </Card>
            )}

            {/* Balance Sheet — borrower-fillable financial snapshot */}
            {application.product_type !== 'merchant_processing' && (
              <Card className="lg:col-span-3" data-testid="balance-sheet-section">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5 text-cyan-700" />
                    Balance Sheet
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <BalanceSheetCard
                    applicationId={application.id}
                    data={(liveApp || application).balance_sheet_data || {}}
                    uploadedFiles={(liveApp || application).balance_sheet_files || []}
                    authToken={localStorage.getItem('auth_token')}
                    businessName={application.business_name || application.applicant_name}
                    onSave={() => refreshApplicationDocs()}
                  />
                </CardContent>
              </Card>
            )}

            {/* Required Documents — Upload & Download for all roles */}
            {liveRequiredDocs.length > 0 && (
              <Card className="lg:col-span-3" data-testid="required-documents-section">
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <FileText className="h-5 w-5" />
                    Required Documents
                    <Badge variant="outline" className="ml-2">
                      {liveRequiredDocs.filter(d => d.uploaded).length}/{liveRequiredDocs.length} uploaded
                    </Badge>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={refreshApplicationDocs}
                      disabled={refreshingDocs}
                      className="ml-auto h-7 text-xs text-slate-500 hover:text-slate-700"
                      data-testid="refresh-docs-btn"
                    >
                      <RefreshCw className={`h-3.5 w-3.5 mr-1 ${refreshingDocs ? 'animate-spin' : ''}`} />
                      Refresh
                    </Button>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <DocumentUploader
                    applicationId={application.id}
                    requiredDocuments={liveRequiredDocs}
                    authToken={localStorage.getItem('auth_token')}
                    onUploadComplete={() => refreshApplicationDocs()}
                    debtSchedule={liveDebtSchedule}
                    debtSchedulePdfUrl={liveDebtPdfUrl}
                    onDebtScheduleSave={() => refreshApplicationDocs()}
                    documentLink={application.document_link || docLink}
                    pfsData={livePfsData}
                    pfsPdfUrl={livePfsPdfUrl}
                    onPfsSave={() => refreshApplicationDocs()}
                    canPushPfs={canManageDocs || isBroker}
                    additionalDocuments={(liveApp || application).additional_documents || []}
                    form8821s={(liveApp || application).form_8821s || []}
                    isUnderwriter={canManageDocs}
                    canDeleteFiles={canDeleteFiles}
                    productType={application.product_type}
                    merchantPreAppData={(liveApp || application).merchant_pre_app_data || {}}
                    additionalMerchantPreApps={(liveApp || application).additional_merchant_pre_apps || []}
                    onMerchantPreAppSave={() => refreshApplicationDocs()}
                  />
                </CardContent>
              </Card>
            )}

            {/* Jumbo Term Loan — Additional Owners (multi-owner + e-sign) */}
            {application.product_type === 'jumbo_term_loan' && (canManageDocs || isBroker) && (
              <JumboAdditionalOwners
                application={liveApp || application}
                authToken={localStorage.getItem('auth_token')}
                onUpdated={() => refreshApplicationDocs()}
              />
            )}

            {/* Missing Documents - Only for underwriters/admins */}
            {canManageDocs && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-orange-500" />
                    Documents Needed
                    <Badge variant="outline" className="ml-auto text-orange-600 border-orange-200">
                      {missingDocs.length}
                    </Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 mb-3">
                    {missingDocs.length === 0 && (
                      <p className="text-sm text-slate-400 py-2">No documents flagged as missing</p>
                    )}
                    {missingDocs.map((doc, i) => (
                      <div key={i} className="flex items-center justify-between gap-2 px-3 py-2 bg-orange-50 border border-orange-200 rounded-md text-sm" data-testid={`review-missing-doc-${i}`}>
                        <span className="text-orange-800">{doc}</span>
                        <button onClick={() => removeMissingDoc(i)} className="text-orange-400 hover:text-red-500 flex-shrink-0">
                          <Trash2 className="h-3.5 w-3.5" />
                        </button>
                      </div>
                    ))}
                  </div>
                  <div className="flex items-center gap-2 mb-3">
                    <Input
                      value={newDoc}
                      onChange={(e) => setNewDoc(e.target.value)}
                      placeholder="Add document name..."
                      className="flex-1 h-8 text-sm"
                      onKeyDown={(e) => e.key === 'Enter' && addMissingDoc()}
                      data-testid="review-missing-doc-input"
                    />
                    <Button variant="outline" size="sm" onClick={addMissingDoc} disabled={!newDoc.trim()} className="h-8" data-testid="review-add-missing-doc-btn">
                      <Plus className="h-3.5 w-3.5" />
                    </Button>
                  </div>
                  {docsChanged && (
                    <Button
                      size="sm"
                      className="w-full bg-orange-600 hover:bg-orange-700"
                      onClick={saveMissingDocs}
                      disabled={savingDocs}
                      data-testid="save-missing-docs-btn"
                    >
                      <Save className="h-4 w-4 mr-2" />
                      {savingDocs ? 'Saving...' : 'Save & Notify Broker'}
                    </Button>
                  )}
                  {(missingDocs.length > 0 || (application?.required_documents || []).some(d => !d.uploaded)) && (
                    <div className="flex gap-2 mt-3 pt-3 border-t border-slate-100">
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 text-xs border-slate-300 hover:bg-slate-50"
                        onClick={async () => {
                          try {
                            await axios.post(`${BACKEND_URL}/api/email/missing-docs/${application.id}`, { recipient_type: 'broker' });
                            toast.success('Missing docs email sent to broker');
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Failed to send email');
                          }
                        }}
                        data-testid="email-broker-missing-docs"
                      >
                        <Mail className="h-3.5 w-3.5 mr-1" /> Email Broker
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        className="flex-1 text-xs border-slate-300 hover:bg-slate-50"
                        onClick={async () => {
                          try {
                            await axios.post(`${BACKEND_URL}/api/email/missing-docs/${application.id}`, { recipient_type: 'borrower' });
                            toast.success('Missing docs email sent to borrower');
                          } catch (err) {
                            toast.error(err.response?.data?.detail || 'Failed to send email');
                          }
                        }}
                        data-testid="email-borrower-missing-docs"
                      >
                        <Mail className="h-3.5 w-3.5 mr-1" /> Email Borrower
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Show missing docs read-only for brokers */}
            {isBroker && missingDocs.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <AlertCircle className="h-5 w-5 text-orange-500" />
                    Documents Needed
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {missingDocs.map((doc, i) => (
                      <div key={i} className="px-3 py-2 bg-orange-50 border border-orange-200 rounded-md text-sm text-orange-800">
                        {doc}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Request Information Panel */}
            <RequestInfoPanel application={application} canManage={canManageDocs} isBroker={isBroker} />

            {/* Borrower Submission History */}
            {application.borrower_submissions?.length > 0 && (
              <Card className="border-blue-200 bg-blue-50" data-testid="borrower-submissions">
                <CardHeader>
                  <CardTitle className="text-base flex items-center gap-2">
                    <User className="h-4 w-4 text-blue-600" />
                    Borrower Responses
                    <Badge variant="secondary" className="ml-auto bg-blue-100 text-blue-700">{application.borrower_submissions.length}</Badge>
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  {application.borrower_submissions.map((sub, idx) => (
                    <div key={idx} className="text-sm border-b border-blue-100 pb-2 last:border-0 last:pb-0">
                      <p className="text-xs text-blue-500 mb-1">
                        {new Date(sub.submitted_at).toLocaleDateString()} {new Date(sub.submitted_at).toLocaleTimeString([], {hour: 'numeric', minute: '2-digit'})}
                      </p>
                      {sub.fields_updated?.length > 0 && (
                        <p className="text-blue-800">
                          <span className="font-medium">Fields updated:</span> {sub.fields_updated.map(f => f.replace(/_/g, ' ')).join(', ')}
                        </p>
                      )}
                      {sub.custom_responses?.length > 0 && sub.custom_responses.map((cr, i) => (
                        <div key={i} className="mt-1 pl-3 border-l-2 border-blue-200">
                          <p className="text-xs font-medium text-blue-600">{cr.label}</p>
                          <p className="text-blue-800">{cr.response}</p>
                        </div>
                      ))}
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Broker Assignment */}
            {canManageDocs && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <UserCheck className="h-5 w-5" />
                    Assign Broker
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {application.broker_id ? (
                      <div className="p-2 bg-blue-50 border border-blue-200 rounded">
                        <p className="text-sm text-blue-800">
                          <strong>Currently Assigned:</strong>{' '}
                          {brokers.find(b => b.id === application.broker_id)?.name || 'Unknown'}
                        </p>
                      </div>
                    ) : (
                      <div className="p-2 bg-orange-50 border border-orange-200 rounded">
                        <p className="text-sm text-orange-800 font-medium">Unassigned</p>
                      </div>
                    )}
                    <Select value={selectedBroker} onValueChange={setSelectedBroker}>
                      <SelectTrigger data-testid="assign-broker-select">
                        <SelectValue placeholder="Select broker" />
                      </SelectTrigger>
                      <SelectContent>
                        {brokers.map((broker) => (
                          <SelectItem key={broker.id} value={broker.id}>
                            {broker.name} {broker.company_name ? `(${broker.company_name})` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      size="sm"
                      onClick={handleAssignBroker}
                      disabled={updating || !selectedBroker || selectedBroker === application.broker_id}
                      className="w-full"
                      data-testid="assign-broker-btn"
                    >
                      {updating ? 'Assigning...' : 'Assign Broker'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Borrower Assignment */}
            {canManageDocs && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5" />
                    Assign Borrower
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-3">
                    {application.borrower_id ? (
                      <div className="p-2 bg-green-50 border border-green-200 rounded">
                        <p className="text-sm text-green-800">
                          <strong>Currently Assigned:</strong>{' '}
                          {borrowers.find(b => b.id === application.borrower_id)?.name || 'Unknown'}
                        </p>
                      </div>
                    ) : (
                      <div className="p-2 bg-orange-50 border border-orange-200 rounded">
                        <p className="text-sm text-orange-800 font-medium">No borrower assigned</p>
                      </div>
                    )}
                    <Select value={selectedBorrower} onValueChange={setSelectedBorrower}>
                      <SelectTrigger data-testid="assign-borrower-select">
                        <SelectValue placeholder="Select borrower" />
                      </SelectTrigger>
                      <SelectContent>
                        {borrowers.map((b) => (
                          <SelectItem key={b.id} value={b.id}>
                            {b.name} ({b.email})
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button
                      size="sm"
                      onClick={handleAssignBorrower}
                      disabled={updating || !selectedBorrower || selectedBorrower === application.borrower_id}
                      className="w-full"
                      data-testid="assign-borrower-btn"
                    >
                      {updating ? 'Assigning...' : 'Assign Borrower'}
                    </Button>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Private Notes */}
            <Card className="lg:col-span-3" data-testid="private-notes-section">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <StickyNote className="h-5 w-5 text-amber-600" />
                  {isBroker ? 'My Private Notes' : 'Internal Notes'}
                  <Badge variant="outline" className="ml-auto text-amber-700 border-amber-200 bg-amber-50">
                    {notes.length}
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-xs text-slate-500 mb-3">
                  {isBroker
                    ? 'These notes are private and visible only to you.'
                    : 'Visible to all admins and underwriters. Not visible to brokers or borrowers.'}
                </p>

                {/* Add new note */}
                <div className="flex gap-2 mb-4">
                  <textarea
                    value={newNote}
                    onChange={(e) => setNewNote(e.target.value)}
                    placeholder="Write a note..."
                    className="flex-1 min-h-[60px] p-2 border border-slate-200 rounded-md text-sm resize-none focus:outline-none focus:ring-1 focus:ring-amber-400 text-slate-800"
                    data-testid="new-note-input"
                  />
                  <Button
                    size="sm"
                    onClick={handleCreateNote}
                    disabled={savingNote || !newNote.trim()}
                    className="self-end bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="save-note-btn"
                  >
                    <Send className="h-4 w-4 mr-1" />
                    {savingNote ? '...' : 'Add'}
                  </Button>
                </div>

                {/* Notes list */}
                <div className="space-y-2 max-h-[200px] overflow-y-auto">
                  {notes.length === 0 && (
                    <p className="text-sm text-slate-400 py-2 text-center">No notes yet</p>
                  )}
                  {notes.map((note) => (
                    <div key={note.id} className="p-3 bg-amber-50/50 border border-amber-100 rounded-lg" data-testid={`note-${note.id}`}>
                      {editingNoteId === note.id ? (
                        <div className="space-y-2">
                          <textarea
                            value={editNoteContent}
                            onChange={(e) => setEditNoteContent(e.target.value)}
                            className="w-full min-h-[50px] p-2 border border-amber-200 rounded text-sm resize-none focus:outline-none focus:ring-1 focus:ring-amber-400 text-slate-800"
                            data-testid={`edit-note-input-${note.id}`}
                          />
                          <div className="flex gap-1 justify-end">
                            <Button size="sm" variant="ghost" onClick={() => setEditingNoteId(null)} className="h-7 text-xs">Cancel</Button>
                            <Button size="sm" onClick={() => handleUpdateNote(note.id)} className="h-7 text-xs bg-amber-600 hover:bg-amber-700 text-white" data-testid={`save-edit-note-${note.id}`}>Save</Button>
                          </div>
                        </div>
                      ) : (
                        <>
                          <div className="flex items-start justify-between gap-2">
                            <p className="text-sm text-slate-700 whitespace-pre-wrap flex-1">{note.content}</p>
                            <div className="flex gap-1 flex-shrink-0">
                              <button onClick={() => { setEditingNoteId(note.id); setEditNoteContent(note.content); }} className="text-slate-400 hover:text-amber-600 p-0.5" data-testid={`edit-note-${note.id}`}>
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                              <button onClick={() => handleDeleteNote(note.id)} className="text-slate-400 hover:text-red-500 p-0.5" data-testid={`delete-note-${note.id}`}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </div>
                          <p className="text-xs text-slate-400 mt-1">
                            {note.author_name} ({note.author_role}) — {new Date(note.created_at).toLocaleString()}
                            {note.updated_at && ' (edited)'}
                          </p>
                        </>
                      )}
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Share Underwriting File - only for underwriters/admins */}
            {(user?.roles?.includes('underwriter') || user?.roles?.includes('admin')) && (
              <UnderwritingShareSection applicationId={application.id} token={token} />
            )}

            {/* Convert Program Type - only for underwriters/admins */}
            {(user?.roles?.includes('underwriter') || user?.roles?.includes('admin')) && (
              <ConvertProgramSection application={application} token={token} onConvert={() => { onStatusUpdate(application.id, application.status); onClose(); }} />
            )}

            {/* Duplicate Application - create a NEW application of a different program type
                from this one's data; original is preserved. Underwriters / admins / brokers. */}
            {(user?.roles?.includes('underwriter') || user?.roles?.includes('admin') || user?.roles?.includes('broker')) && (
              <DuplicateProgramSection application={application} token={token} onDuplicated={() => { onStatusUpdate(application.id, application.status); onClose(); }} />
            )}

            {/* For apps created via "Create Another Application" — retroactively pull
                over uploaded docs from the source application. Idempotent + safe to
                re-run. Only shows when there IS a source. */}
            {application?.duplicated_from && (user?.roles?.includes('underwriter') || user?.roles?.includes('admin') || user?.roles?.includes('broker')) && (
              <CopyFilesFromSourceSection application={application} token={token} onCopied={() => { onStatusUpdate(application.id, application.status); }} />
            )}

            {/* Duplicate-file cleanup: same file entry appearing in multiple slots
                (root cause: buggy pre-Feb-2026 startup scanner). Shows only when
                duplicates are actually present. */}
            {(user?.roles?.includes('underwriter') || user?.roles?.includes('admin')) && (
              <DedupeDocumentFilesSection application={application} token={token} onFixed={() => { onStatusUpdate(application.id, application.status); }} />
            )}

            {/* Rescue misplaced files after a legacy convert (e.g., bank statements stuck
                in additional_documents because slot names didn't match across templates). */}
            {(user?.roles?.includes('underwriter') || user?.roles?.includes('admin')) && (application.additional_documents || []).some((d) => (d.files || []).length > 0) && (
              <RescueDocSlotsSection application={application} token={token} onRescued={() => { onStatusUpdate(application.id, application.status); onClose(); }} />
            )}

            {/* Borrower Share Links */}
            <BorrowerShareLinksSection applicationId={application.id} />

            {/* All Application Fields - comprehensive read-only view of every field
                captured for this application so brokers and underwriters can quickly
                see what's filled in and what's still missing. */}
            <ApplicationFieldsPanel application={liveApp || application} />

            {/* Business Information */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Building2 className="h-5 w-5" />
                  Business Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label>Business Name</Label>
                  <p className="font-medium">{getBusinessName()}</p>
                </div>
                <div>
                  <Label>Address</Label>
                  <p>{getBusinessAddress()}</p>
                </div>
                <div>
                  <Label>Industry</Label>
                  <p>
                    {application.term_loan_data?.industry_type ||
                     application.term_loan_10yr_data?.industry_type ||
                     application.term_loc_hybrid_data?.industry_type ||
                     application.term_loan_3_5yr_data?.industry_type ||
                     application.sba_loan_data?.naics_code_or_nature ||
                     application.equipment_financing_data?.industry ||
                     'Not specified'}
                  </p>
                </div>
                <div>
                  <Label>Business Type</Label>
                  <p>
                    {application.term_loan_data?.entity_type ||
                     application.term_loan_10yr_data?.entity_type ||
                     application.term_loc_hybrid_data?.entity_type ||
                     application.term_loan_3_5yr_data?.entity_type ||
                     application.sba_loan_data?.legal_entity_type ||
                     application.line_of_credit_data?.business_type ||
                     application.equipment_financing_data?.entity_type ||
                     'Not specified'}
                  </p>
                </div>
              </CardContent>
            </Card>

            {/* Primary Contact */}
            {contact && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5" />
                    Primary Owner / Contact
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-3">
                  <div>
                    <Label>Name</Label>
                    <p className="font-medium">{contact.name || 'Not provided'}</p>
                  </div>
                  {contact.ownership && (
                    <div>
                      <Label>Ownership</Label>
                      <p>{contact.ownership}%</p>
                    </div>
                  )}
                  <div>
                    <Label>Email</Label>
                    <p>{contact.email || 'Not provided'}</p>
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <p>{contact.phone || 'Not provided'}</p>
                  </div>
                  {contact.dob && (
                    <div>
                      <Label>Date of Birth</Label>
                      <p>{contact.dob}</p>
                    </div>
                  )}
                  {contact.ssn && (
                    <div>
                      <Label>SSN</Label>
                      <p><SSNToggle value={contact.ssn} /></p>
                    </div>
                  )}
                  {contact.address && (
                    <div>
                      <Label>Home Address</Label>
                      <p>{contact.address}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            {/* Additional Owners */}
            {getAdditionalOwners().length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <User className="h-5 w-5" />
                    Additional Owners ({getAdditionalOwners().length})
                  </CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  {getAdditionalOwners().map((owner, i) => (
                    <div key={i} className="p-3 bg-slate-50 border border-slate-200 rounded-lg space-y-2">
                      <div className="flex items-center justify-between">
                        <p className="font-medium text-sm">{`${owner.first_name || ''} ${owner.last_name || ''}`.trim() || owner.first_last_name || 'Unnamed'}</p>
                        <span className="text-xs bg-slate-200 px-2 py-0.5 rounded">{owner.percentage_of_ownership || 0}%</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2 text-xs text-slate-600">
                        {owner.email_address && <div><span className="text-slate-400">Email:</span> {owner.email_address}</div>}
                        {owner.mobile_phone_number && <div><span className="text-slate-400">Phone:</span> {owner.mobile_phone_number}</div>}
                        {owner.date_of_birth && <div><span className="text-slate-400">DOB:</span> {owner.date_of_birth}</div>}
                        {owner.social_security_number && <div><span className="text-slate-400">SSN:</span> <SSNToggle value={owner.social_security_number} /></div>}
                        {owner.home_address && <div><span className="text-slate-400">Address:</span> {owner.home_address}</div>}
                      </div>
                    </div>
                  ))}
                </CardContent>
              </Card>
            )}

            {/* Loan Details */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <DollarSign className="h-5 w-5" />
                  Loan Details
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div>
                  <Label>Purpose</Label>
                  <p>
                    {application.term_loan_data?.use_of_funds_description ||
                     application.term_loan_10yr_data?.use_of_funds_description ||
                     application.term_loc_hybrid_data?.use_of_funds_description ||
                     application.term_loan_3_5yr_data?.use_of_funds_description ||
                     application.sba_loan_data?.additional_notes ||
                     application.line_of_credit_data?.purpose_of_credit ||
                     application.line_of_credit_data?.intended_use_of_funds ||
                     application.equipment_financing_data?.equipment_description ||
                     'Purpose not specified'}
                  </p>
                </div>
                {application.equipment_financing_data && (
                  <>
                    <div>
                      <Label>Equipment Type</Label>
                      <p>{application.equipment_financing_data.equipment_type || 'Not specified'}</p>
                    </div>
                    <div>
                      <Label>Equipment Cost</Label>
                      <p>${(application.equipment_financing_data.equipment_cost || 0).toLocaleString()}</p>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>

            {/* Business Financials - SBA only */}
            {application.sba_loan_data && (application.sba_loan_data.revenue || application.sba_loan_data.fiscal_year || application.sba_loan_data.cost_of_goods_sold) && (() => {
              const d = application.sba_loan_data;
              const revenue = parseFloat(d.revenue) || 0;
              const cogs = parseFloat(d.cost_of_goods_sold) || 0;
              const payroll = parseFloat(d.payroll) || 0;
              const rent = parseFloat(d.rent) || 0;
              const otherExp = parseFloat(d.other_operating_expenses) || 0;
              const grossMargin = revenue - cogs;
              const operatingCashFlow = grossMargin - payroll - rent - otherExp;
              const fmtCurrency = (v) => `$${(parseFloat(v) || 0).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
              return (
                <Card className="lg:col-span-3" data-testid="business-financials-review">
                  <CardContent className="pt-5">
                    <div className="mb-4">
                      <h3 className="text-sm font-semibold text-slate-600 flex items-center gap-2">
                        <DollarSign className="h-4 w-4" /> Business Financials — Last Reported Fiscal Year
                      </h3>
                      {d.fiscal_year && (
                        <p className="text-xs text-slate-500 mt-0.5" data-testid="business-financials-reported-year">
                          Borrower reported: FY {d.fiscal_year}
                        </p>
                      )}
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 gap-4 mb-4">
                      <div><Label className="text-xs">Revenue</Label><p className="font-medium">{fmtCurrency(d.revenue)}</p></div>
                      <div><Label className="text-xs">Cost of Goods Sold</Label><p className="font-medium">{fmtCurrency(d.cost_of_goods_sold)}</p></div>
                      <div><Label className="text-xs">Payroll</Label><p className="font-medium">{fmtCurrency(d.payroll)}</p></div>
                      <div><Label className="text-xs">Rent</Label><p className="font-medium">{fmtCurrency(d.rent)}</p></div>
                      <div><Label className="text-xs">Other Operating Expenses</Label><p className="font-medium">{fmtCurrency(d.other_operating_expenses)}</p></div>
                    </div>
                    <div className="grid grid-cols-2 gap-4 pt-3 border-t border-slate-200">
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-slate-500">Gross Margin</p>
                        <p className={`text-lg font-bold ${grossMargin >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{fmtCurrency(grossMargin)}</p>
                      </div>
                      <div className="p-3 bg-slate-50 rounded-lg">
                        <p className="text-xs text-slate-500">Operating Cash Flow</p>
                        <p className={`text-lg font-bold ${operatingCashFlow >= 0 ? 'text-emerald-700' : 'text-red-600'}`}>{fmtCurrency(operatingCashFlow)}</p>
                      </div>
                    </div>
                    {d.financial_attestation && (
                      <p className="mt-3 text-xs text-emerald-600 flex items-center gap-1"><CheckCircle className="h-3 w-3" /> Financial attestation confirmed</p>
                    )}
                  </CardContent>
                </Card>
              );
            })()}

            {/* AI Document Analysis Panel — pick any uploaded file and run
                Tax Return Analysis or Bank Statement Analysis on it.
                Results appear in the Analysis & Reports section above. */}
            {(isAdmin || isUnderwriter || isJrUnderwriter) && (
              <div className="lg:col-span-3">
                <DocumentAnalysisPanel
                  application={liveApp || application}
                  authToken={localStorage.getItem('auth_token')}
                  onComplete={() => refreshApplicationDocs && refreshApplicationDocs()}
                />
              </div>
            )}
          </div>

          {updating && (
            <Alert className="mt-6">
              <AlertDescription>
                Updating application...
              </AlertDescription>
            </Alert>
          )}
        </div>
      </div>

      {/* Reminder Modal — appears after underwriter sets status to broker_attention_needed */}
      {reminderModalOpen && (
        <div
          className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4"
          data-testid="reminder-modal"
        >
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                <Bell className="h-5 w-5 text-amber-700" />
              </div>
              <div>
                <h3 className="text-lg font-bold text-slate-900">Schedule a Reminder</h3>
                <p className="text-sm text-slate-700 font-medium">A task and email will be created at the chosen time.</p>
              </div>
            </div>
            <div className="space-y-4">
              <div>
                <Label htmlFor="reminder-when" className="text-sm font-bold text-slate-900">When</Label>
                <Input
                  id="reminder-when"
                  type="datetime-local"
                  value={reminderWhen}
                  onChange={(e) => setReminderWhen(e.target.value)}
                  className="mt-1 text-slate-900 font-semibold border-slate-400"
                  data-testid="reminder-when-input"
                />
              </div>
              <div>
                <Label htmlFor="reminder-note" className="text-sm font-bold text-slate-900">Note (optional)</Label>
                <textarea
                  id="reminder-note"
                  value={reminderNote}
                  onChange={(e) => setReminderNote(e.target.value)}
                  rows={4}
                  className="w-full mt-1 rounded-md border border-slate-400 p-2 text-sm text-slate-900 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-slate-400"
                  placeholder="What should you remember to check on?"
                  data-testid="reminder-note-input"
                />
              </div>
            </div>
            <div className="flex gap-2 mt-6">
              <Button
                variant="outline"
                className="flex-1"
                onClick={cancelReminder}
                disabled={savingReminder}
                data-testid="reminder-skip-btn"
              >
                Skip
              </Button>
              <Button
                className="flex-1 bg-amber-600 hover:bg-amber-700 text-white"
                onClick={submitReminder}
                disabled={savingReminder}
                data-testid="reminder-save-btn"
              >
                {savingReminder ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Scheduling...</> : <>Set Reminder</>}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const BorrowerShareLinksSection = ({ applicationId }) => {
  const [links, setLinks] = useState([]);
  const [copiedId, setCopiedId] = useState(null);
  const [creating, setCreating] = useState(false);
  const { user } = useAuth();

  const fetchLinks = useCallback(async () => {
    try {
      const res = await axios.get(`${BACKEND_URL}/api/borrower-links/${applicationId}`);
      setLinks(res.data.links || res.data || []);
    } catch {
      try {
        const res2 = await axios.get(`${BACKEND_URL}/api/applications/${applicationId}/borrower-shares`);
        setLinks(res2.data || []);
      } catch { /* silent */ }
    }
  }, [applicationId]);

  useEffect(() => {
    fetchLinks();
  }, [fetchLinks]);

  const createLink = async () => {
    setCreating(true);
    try {
      await axios.post(`${BACKEND_URL}/api/borrower-links?broker_id=${user.id}&application_id=${applicationId}&mode=completion`);
      await fetchLinks();
      toast.success('Borrower link created');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create borrower link');
    } finally {
      setCreating(false);
    }
  };

  const copyLink = (token, id) => {
    navigator.clipboard.writeText(`${window.location.origin}/borrower/${token}`);
    setCopiedId(id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  return (
    <Card data-testid="borrower-share-links-section">
      <CardHeader className="pb-3">
        <CardTitle className="flex items-center justify-between text-base">
          <span className="flex items-center gap-2">
            <Share2 className="h-4 w-4" />
            Borrower Completion Links
          </span>
          <Button
            size="sm"
            variant="outline"
            onClick={createLink}
            disabled={creating}
            data-testid="create-borrower-link-btn"
            title="Generate a link your borrower can use to fill in any missing information on this application."
          >
            {creating ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Creating...</> : <><Plus className="h-3.5 w-3.5 mr-1" /> Generate Borrower Link</>}
          </Button>
        </CardTitle>
        <p className="text-xs text-slate-500 mt-1">
          Share this link with your borrower so they can complete any missing application information. Their changes will not overwrite anything you've already entered.
        </p>
      </CardHeader>
      <CardContent>
        {links.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-3">No borrower links yet. Click "Generate Borrower Link" to create one you can send.</p>
        ) : (
          <div className="space-y-2">
            {links.map((link) => (
              <div key={link.id} className="flex items-center gap-3 p-3 bg-slate-50 rounded-lg border border-slate-100" data-testid={`borrower-link-${link.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-sm font-medium text-slate-800 truncate">
                      {link.borrower_email || 'No email (copy link)'}
                    </span>
                    {link.borrower_started ? (
                      <Badge className="bg-green-100 text-green-700 text-[10px]">Borrower Started</Badge>
                    ) : (
                      <Badge className="bg-slate-100 text-slate-500 text-[10px]">Not Started</Badge>
                    )}
                  </div>
                  <p className="text-xs text-slate-400 mt-0.5">
                    Shared {new Date(link.created_at).toLocaleDateString()}
                    {link.borrower_last_saved && (
                      <> · Last saved {new Date(link.borrower_last_saved).toLocaleDateString()}</>
                    )}
                  </p>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyLink(link.token, link.id)}
                  className="flex-shrink-0 gap-1"
                  data-testid={`copy-borrower-link-${link.id}`}
                >
                  {copiedId === link.id ? <Check className="h-3 w-3" /> : <Copy className="h-3 w-3" />}
                  {copiedId === link.id ? 'Copied' : 'Copy'}
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => window.open(`/borrower/${link.token}`, '_blank')}
                  className="flex-shrink-0"
                  data-testid={`open-borrower-link-${link.id}`}
                >
                  <ExternalLink className="h-3.5 w-3.5" />
                </Button>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const TOOL_TYPE_LABELS = {
  tax_return_analysis: 'Tax Return Analysis',
  bank_statement_analysis: 'Bank Statement Analysis',
  credit_analyzer: 'Business Credit Analysis',
  amortization: 'Amortization Schedule',
  pre_underwriting: 'Pre-Underwriting Assessment',
};

const TOOL_TYPE_ICONS = {
  tax_return_analysis: FileText,
  bank_statement_analysis: DollarSign,
  credit_analyzer: BarChart3,
  amortization: DollarSign,
  pre_underwriting: CheckCircle,
};

const fmt$ = (v) => {
  if (v == null || v === '') return '—';
  return `$${Number(v).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
};

const ToolkitResultsSection = ({ applicationId, token, canRefreshGuidelines = false }) => {
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  // Bump per-result to force tax/bank-analysis rows to recompute against the latest
  // qualification thresholds. The underlying data doesn't change; only the
  // inline math in `renderResultData` re-runs. `refreshedAt` is keyed by
  // result.id so refreshing one card doesn't light up the banner on others.
  const [guidelineRefreshAt, setGuidelineRefreshAt] = useState({});
  const [refreshedAt, setRefreshedAt] = useState({});
  const API = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    const fetchResults = async () => {
      try {
        const { data } = await axios.get(`${API}/api/applications/${applicationId}/toolkit-results`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        const list = data.results || [];
        setResults(list);
        // Auto-expand the most recent analysis so the underwriter sees the
        // financials & eligibility verdicts immediately without an extra click.
        if (list.length > 0) {
          const newest = list.reduce(
            (acc, r) => (!acc || new Date(r.created_at) > new Date(acc.created_at) ? r : acc),
            null
          );
          if (newest) setExpandedId(newest.id);
        }
      } catch { /* silent */ }
      finally { setLoading(false); }
    };
    fetchResults();
  }, [applicationId, token, API]);

  const handleDelete = async (resultId) => {
    if (!window.confirm('Remove this analysis from the application?')) return;
    try {
      await axios.delete(`${API}/api/applications/${applicationId}/toolkit-results/${resultId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResults(prev => prev.filter(r => r.id !== resultId));
    } catch { /* silent */ }
  };

  const handleClearAll = async () => {
    if (results.length === 0) return;
    if (!window.confirm(`Clear ALL ${results.length} analyses for this application? This resets Analysis & Reports to an empty state and cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/api/applications/${applicationId}/toolkit-results`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setResults([]);
      setExpandedId(null);
      setRefreshedAt({});
      setGuidelineRefreshAt({});
    } catch (err) {
      alert(err?.response?.data?.detail || 'Failed to clear analyses');
    }
  };

  const renderResultData = (toolType, data) => {
    if (!data || typeof data !== 'object') return null;

    if (toolType === 'tax_return_analysis') {
      // Cashflow qualification calculations (matching toolkit logic)
      const revenue = data.gross_receipts_or_revenue || 0;
      const cogs = data.cost_of_goods_sold || 0;
      const grossProfit = data.gross_profit || (revenue - cogs);
      const officerComp = data.officer_compensation || 0;
      const guaranteedPayments = data.guaranteed_payments_to_partners || 0;
      const interestExp = data.interest_expense || 0;
      const depreciation = data.depreciation || 0;
      const netIncome = data.net_income_or_loss || 0;
      const existingDebt = data.existing_annual_debt_service || 0;
      const formType = String(data.form_type || '').toLowerCase();

      // Textbook EBITDA = Net Income + Interest + Taxes + Depreciation + Amortization.
      // We don't extract entity-level taxes (1065/1120-S/Schedule C pay no entity
      // income tax; 1120 sometimes does but we don't currently extract Line 31)
      // or amortization separately yet, so EBITDA here = NI + Interest + Depreciation.
      const ebitda = netIncome + interestExp + depreciation;

      // Adjusted EBITDA (a.k.a. Cash Flow Available for Debt Service) ADDS owner
      // pay back as a discretionary add-back. Used for DSCR sizing because the
      // owner can reduce their comp to service new debt.
      // Form-type-aware add-back:
      //   - 1065 partnerships: ONLY guaranteed_payments_to_partners (Line 10).
      //     We deliberately IGNORE officer_compensation for 1065 to prevent
      //     salaries-misclassification from inflating cash flow by $1M+.
      //   - Schedule C / 1040 sole prop: owner pay already in net income; no add-back.
      //   - 1120 / 1120-S: officer_compensation (Line 7 / Line 12 of those forms).
      const isPartnership = formType.includes('1065') || formType.includes('partner');
      const isSoleProp = formType.includes('schedule c') || (formType.includes('1040') && !formType.includes('1041'));
      let ownerCompAddBack;
      if (isPartnership) {
        ownerCompAddBack = guaranteedPayments;
      } else if (isSoleProp) {
        ownerCompAddBack = 0;
      } else {
        ownerCompAddBack = officerComp;
      }
      const adjustedEbitda = ebitda + ownerCompAddBack;

      const annualRate = 0.18;
      const monthlyRate = annualRate / 12;
      const calcMonthlyPayment = (principal, termMonths) => {
        if (principal <= 0 || termMonths <= 0) return 0;
        return principal * (monthlyRate * Math.pow(1 + monthlyRate, termMonths)) / (Math.pow(1 + monthlyRate, termMonths) - 1);
      };
      const calcMaxLoanFromDSCR = (minDSCR, termMonths) => {
        const maxADS = adjustedEbitda / minDSCR;
        const available = maxADS - existingDebt;
        if (available <= 0) return 0;
        const pvFactor = (1 - Math.pow(1 + monthlyRate, -termMonths)) / monthlyRate;
        return (available / 12) * pvFactor;
      };

      const maxLoan10yr_dscr = calcMaxLoanFromDSCR(1.25, 120);
      const maxLoan10yr_rev = revenue * 0.60 - existingDebt;
      const maxLoan10yr = Math.max(0, Math.min(maxLoan10yr_dscr, maxLoan10yr_rev > 0 ? maxLoan10yr_rev : Infinity, 200000));
      const maxLoan35yr_dscr = calcMaxLoanFromDSCR(1.15, 60);
      const maxLoan35yr_rev = revenue * 0.25;
      const maxLoan35yr = Math.max(0, Math.min(maxLoan35yr_dscr, maxLoan35yr_rev > 0 ? maxLoan35yr_rev : Infinity, 300000));
      const pmt10yr = calcMonthlyPayment(maxLoan10yr, 120);
      const pmt35yr = calcMonthlyPayment(maxLoan35yr, 60);
      const currentDSCR = existingDebt > 0 ? adjustedEbitda / existingDebt : null;
      const dscr10yr = (existingDebt + pmt10yr * 12) > 0 ? adjustedEbitda / (existingDebt + pmt10yr * 12) : null;
      const dscr35yr = (existingDebt + pmt35yr * 12) > 0 ? adjustedEbitda / (existingDebt + pmt35yr * 12) : null;
      const q10yr = maxLoan10yr > 25000 && adjustedEbitda > 0 && revenue >= 100000;
      const q35yr = maxLoan35yr > 10000 && adjustedEbitda > 0;

      // Quidity Jumbo Term Loan (24% simple interest, 24-month term, $10M cap).
      // Sized assuming the new loan can REFINANCE existing debt — so we do NOT
      // subtract existing annual debt service from the DSCR headroom.  This
      // matches how the Jumbo RBF product is sized and reflects the product
      // intent: a recap / refi facility.
      const calcMaxLoanSimpleInterest = (minDSCR, annualRate, termMonths, opts = {}) => {
        const assumeRefinance = opts.assumeRefinance === true;
        const maxAnnualDebtService = adjustedEbitda / minDSCR;
        const availableAnnual = assumeRefinance
          ? maxAnnualDebtService
          : (maxAnnualDebtService - existingDebt);
        if (availableAnnual <= 0) return 0;
        const totalFactor = 1 + annualRate * (termMonths / 12);
        const annualFactor = totalFactor * (12 / termMonths);
        return availableAnnual / annualFactor;
      };
      const calcSimpleInterestMonthly = (principal, annualRate, termMonths) => {
        if (principal <= 0 || termMonths <= 0) return 0;
        return (principal * (1 + annualRate * (termMonths / 12))) / termMonths;
      };
      const maxJumboTL_dscr = calcMaxLoanSimpleInterest(1.25, 0.24, 24, { assumeRefinance: true });
      const maxJumboTL_revenue = revenue * 0.50;
      const maxJumboTL = Math.max(0, Math.min(
        maxJumboTL_dscr,
        maxJumboTL_revenue > 0 ? maxJumboTL_revenue : Infinity,
        10000000
      ));
      // EBITDA margin uses the TRUE ebitda (textbook). This is a quality-of-earnings
      // signal that shouldn't be inflated by owner-comp add-backs.
      const ebitdaMargin = revenue > 0 ? (ebitda / revenue) : 0;
      const qJumboTL = maxJumboTL > 25000 && adjustedEbitda > 0 && revenue >= 3000000 && ebitdaMargin >= 0.03;
      const pmtJumboTL = calcSimpleInterestMonthly(maxJumboTL, 0.24, 24);
      // DSCR shown is post-refi (new debt replaces existing, so existing is excluded).
      const dscrJumboTL = pmtJumboTL > 0 ? adjustedEbitda / (pmtJumboTL * 12) : null;

      // Quidity Jumbo RBF (24% simple interest, 12-month term, $10M cap)
      const maxJumboRBF_revenue = revenue * 0.50;
      const annualizedRBF_cap = adjustedEbitda > 0 ? adjustedEbitda / 1.24 : 0;
      const maxJumboRBF = Math.max(0, Math.min(
        maxJumboRBF_revenue > 0 ? maxJumboRBF_revenue : Infinity,
        annualizedRBF_cap > 0 ? annualizedRBF_cap : Infinity,
        10000000
      ));
      const qJumboRBF = maxJumboRBF > 25000 && adjustedEbitda > 0 && revenue >= 1800000;
      const pmtJumboRBF = calcSimpleInterestMonthly(maxJumboRBF, 0.24, 12);
      const dscrJumboRBF = (existingDebt + pmtJumboRBF * 12) > 0 ? adjustedEbitda / (existingDebt + pmtJumboRBF * 12) : null;

      return (
        <div className="space-y-4">
          {/* Extracted Financials */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
            {data.business_name && <div><span className="text-slate-400 text-xs block">Business</span><span className="font-medium">{data.business_name}</span></div>}
            {data.tax_year && <div><span className="text-slate-400 text-xs block">Tax Year</span><span className="font-medium">{data.tax_year}</span></div>}
            {data.form_type && <div><span className="text-slate-400 text-xs block">Form</span><span className="font-medium">{data.form_type}</span></div>}
            {revenue > 0 && <div><span className="text-slate-400 text-xs block">Revenue</span><span className="font-medium text-emerald-700">{fmt$(revenue)}</span></div>}
            {cogs > 0 && <div><span className="text-slate-400 text-xs block">COGS</span><span className="font-medium">{fmt$(cogs)}</span></div>}
            {grossProfit > 0 && <div><span className="text-slate-400 text-xs block">Gross Profit</span><span className="font-medium text-emerald-700">{fmt$(grossProfit)}</span></div>}
            {officerComp > 0 && <div><span className="text-slate-400 text-xs block">Officer Comp</span><span className="font-medium">{fmt$(officerComp)}</span></div>}
            {interestExp > 0 && <div><span className="text-slate-400 text-xs block">Interest</span><span className="font-medium">{fmt$(interestExp)}</span></div>}
            {depreciation > 0 && <div><span className="text-slate-400 text-xs block">Depreciation</span><span className="font-medium">{fmt$(depreciation)}</span></div>}
            {netIncome !== 0 && <div><span className="text-slate-400 text-xs block">Net Income</span><span className={`font-medium ${netIncome < 0 ? 'text-red-600' : ''}`}>{fmt$(netIncome)}</span></div>}
            {existingDebt > 0 && <div><span className="text-slate-400 text-xs block">Debt Service</span><span className="font-medium text-amber-700">{fmt$(existingDebt)}</span></div>}
          </div>

          {/* DSCR Analysis */}
          {(ebitda !== 0 || adjustedEbitda !== 0) && (
            <div className="border border-slate-200 rounded-lg p-3 bg-slate-50">
              <p className="text-xs font-bold text-slate-600 uppercase mb-2">DSCR Analysis (18% Interest)</p>
              <div className="grid grid-cols-3 gap-2 mb-2">
                <div className="text-center p-2 bg-white rounded border" title="Net Income + Interest + Depreciation + Amortization">
                  <p className="text-[10px] text-slate-500 uppercase">EBITDA</p>
                  <p className={`text-base font-black ${ebitda > 0 ? 'text-emerald-700' : 'text-red-600'}`}>{fmt$(ebitda)}</p>
                  <p className="text-[9px] text-slate-400 mt-0.5">NI + Int + Depr</p>
                </div>
                <div className="text-center p-2 bg-white rounded border" title="EBITDA + Owner-pay add-back (Cash Flow Available for Debt Service)">
                  <p className="text-[10px] text-slate-500 uppercase">Adj. EBITDA</p>
                  <p className={`text-base font-black ${adjustedEbitda > 0 ? 'text-emerald-700' : 'text-red-600'}`}>{fmt$(adjustedEbitda)}</p>
                  <p className="text-[9px] text-slate-400 mt-0.5">+ Owner Pay {ownerCompAddBack > 0 ? `(${fmt$(ownerCompAddBack)})` : ''}</p>
                </div>
                <div className="text-center p-2 bg-white rounded border" title="Adjusted EBITDA / Existing Debt Service">
                  <p className="text-[10px] text-slate-500 uppercase">Current DSCR</p>
                  <p className={`text-base font-black ${currentDSCR === null ? 'text-slate-400' : currentDSCR >= 1.25 ? 'text-emerald-700' : currentDSCR >= 1.0 ? 'text-amber-600' : 'text-red-600'}`}>
                    {currentDSCR !== null ? `${currentDSCR.toFixed(2)}x` : 'N/A'}
                  </p>
                  <p className="text-[9px] text-slate-400 mt-0.5">vs existing debt</p>
                </div>
              </div>
              <p className="text-[10px] text-slate-500 italic">
                DSCR sizing uses <span className="font-semibold">Adjusted EBITDA</span> (a.k.a. Cash Flow Available for Debt Service) because owners can reduce comp to service new debt.
              </p>
            </div>
          )}

          {/* 10 Year Term Loan Eligibility */}
          {ebitda !== 0 && (
            <div className={`border rounded-lg p-3 ${q10yr ? 'border-emerald-200 bg-emerald-50/50' : 'border-red-200 bg-red-50/50'}`}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold text-slate-700 uppercase">10 Year Term Loan</p>
                <Badge className={`text-[10px] ${q10yr ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
                  {q10yr ? 'Potentially Eligible' : 'Does Not Qualify'}
                </Badge>
              </div>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between"><span className="text-slate-600">Max Loan Amount</span><span className="font-bold">{fmt$(maxLoan10yr)}</span></div>
                <div className="flex justify-between"><span className="text-slate-600">Monthly Payment</span><span className="font-bold">{fmt$(pmt10yr)}</span></div>
                <div className="flex justify-between"><span className="text-slate-600">Projected DSCR</span><span className={`font-bold ${dscr10yr && dscr10yr >= 1.25 ? 'text-emerald-700' : 'text-red-600'}`}>{dscr10yr ? `${dscr10yr.toFixed(2)}x` : 'N/A'}</span></div>
              </div>
            </div>
          )}

          {/* 3-5 Year Term Loan Eligibility */}
          {ebitda !== 0 && (
            <div className={`border rounded-lg p-3 ${q35yr ? 'border-emerald-200 bg-emerald-50/50' : 'border-red-200 bg-red-50/50'}`}>
              <div className="flex items-center justify-between mb-2">
                <p className="text-xs font-bold text-slate-700 uppercase">3-5 Year Term Loan</p>
                <Badge className={`text-[10px] ${q35yr ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
                  {q35yr ? 'Potentially Eligible' : 'Does Not Qualify'}
                </Badge>
              </div>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between"><span className="text-slate-600">Max Loan Amount</span><span className="font-bold">{fmt$(maxLoan35yr)}</span></div>
                <div className="flex justify-between"><span className="text-slate-600">Monthly Payment</span><span className="font-bold">{fmt$(pmt35yr)}</span></div>
                <div className="flex justify-between"><span className="text-slate-600">Projected DSCR</span><span className={`font-bold ${dscr35yr && dscr35yr >= 1.15 ? 'text-emerald-700' : 'text-red-600'}`}>{dscr35yr ? `${dscr35yr.toFixed(2)}x` : 'N/A'}</span></div>
              </div>
            </div>
          )}

          {/* Quidity Jumbo Term Loan Eligibility (24% simple / 24-month) */}
          {ebitda !== 0 && (
            <div className={`border rounded-lg p-3 ${qJumboTL ? 'border-emerald-200 bg-emerald-50/50' : 'border-red-200 bg-red-50/50'}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-xs font-bold text-slate-700 uppercase">Quidity Jumbo Term Loan</p>
                  <p className="text-[10px] text-slate-500">24% simple interest · 24-month term · up to $10M · sized assuming refi of existing debt</p>
                </div>
                <Badge className={`text-[10px] ${qJumboTL ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
                  {qJumboTL ? 'Potentially Eligible' : 'Does Not Qualify'}
                </Badge>
              </div>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between"><span className="text-slate-600">Max Loan Amount</span><span className="font-bold">{fmt$(maxJumboTL)}</span></div>
                <div className="flex justify-between"><span className="text-slate-600">Monthly Payment</span><span className="font-bold">{fmt$(pmtJumboTL)}</span></div>
                <div className="flex justify-between"><span className="text-slate-600">Projected DSCR</span><span className={`font-bold ${dscrJumboTL && dscrJumboTL >= 1.25 ? 'text-emerald-700' : 'text-red-600'}`}>{dscrJumboTL ? `${dscrJumboTL.toFixed(2)}x` : 'N/A'}</span></div>
                <div className="flex justify-between"><span className="text-slate-600">EBITDA Margin</span><span className={`font-bold ${ebitdaMargin >= 0.03 ? 'text-emerald-700' : 'text-red-600'}`}>{(ebitdaMargin * 100).toFixed(1)}%</span></div>
              </div>
              <div className="grid grid-cols-2 gap-1.5 text-[11px] mt-2 pt-2 border-t border-slate-200">
                <div className={`flex items-center gap-1.5 ${revenue >= 3000000 ? 'text-emerald-700' : 'text-red-600'}`}>
                  <span className="font-bold">{revenue >= 3000000 ? '✓' : '✗'}</span>
                  <span>$3M annual revenue ({fmt$(revenue)})</span>
                </div>
                <div className={`flex items-center gap-1.5 ${ebitdaMargin >= 0.03 ? 'text-emerald-700' : 'text-red-600'}`}>
                  <span className="font-bold">{ebitdaMargin >= 0.03 ? '✓' : '✗'}</span>
                  <span>3%+ EBITDA margin</span>
                </div>
              </div>
            </div>
          )}

          {/* Quidity Jumbo RBF Eligibility (Revenue-Based Financing, 12-month) */}
          {ebitda !== 0 && (
            <div className={`border rounded-lg p-3 ${qJumboRBF ? 'border-emerald-200 bg-emerald-50/50' : 'border-red-200 bg-red-50/50'}`}>
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-xs font-bold text-slate-700 uppercase">Quidity Jumbo RBF</p>
                  <p className="text-[10px] text-slate-500">Revenue-based · 24% simple / 12 mo · up to $10M</p>
                </div>
                <Badge className={`text-[10px] ${qJumboRBF ? 'bg-emerald-100 text-emerald-800' : 'bg-red-100 text-red-800'}`}>
                  {qJumboRBF ? 'Potentially Eligible' : 'Does Not Qualify'}
                </Badge>
              </div>
              <div className="space-y-1 text-xs">
                <div className="flex justify-between"><span className="text-slate-600">Max Purchase Amount</span><span className="font-bold">{fmt$(maxJumboRBF)}</span></div>
                <div className="flex justify-between"><span className="text-slate-600">Monthly Payment</span><span className="font-bold">{fmt$(pmtJumboRBF)}</span></div>
                <div className="flex justify-between"><span className="text-slate-600">Projected DSCR</span><span className={`font-bold ${dscrJumboRBF && dscrJumboRBF >= 1.0 ? 'text-emerald-700' : 'text-red-600'}`}>{dscrJumboRBF ? `${dscrJumboRBF.toFixed(2)}x` : 'N/A'}</span></div>
              </div>
              <div className="text-[11px] mt-2 pt-2 border-t border-slate-200">
                <div className={`flex items-center gap-1.5 ${revenue >= 1800000 ? 'text-emerald-700' : 'text-red-600'}`}>
                  <span className="font-bold">{revenue >= 1800000 ? '✓' : '✗'}</span>
                  <span>$1.8M+ annual revenue required ({fmt$(revenue)})</span>
                </div>
              </div>
            </div>
          )}

          {/* SBA Loan — Above $350K (up to $5M) */}
          {adjustedEbitda !== 0 && (
            <SBAEligibilityCard
              ebitda={adjustedEbitda}
              revenue={revenue}
              existingBusinessDebt={existingDebt}
              industry={data.industry || data.naics_code_or_nature}
              timeInBusinessYears={data.time_in_business_years || data.years_in_business}
            />
          )}
        </div>
      );
    }

    if (toolType === 'bank_statement_analysis') {
      const months = data.months || [];
      return (
        <div className="space-y-3 text-sm">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {data.account_holder && <div><span className="text-slate-400 text-xs block">Account</span><span className="font-medium">{data.account_holder}</span></div>}
            {data.bank_name && <div><span className="text-slate-400 text-xs block">Bank</span><span className="font-medium">{data.bank_name}</span></div>}
            {data.average_balance != null && <div><span className="text-slate-400 text-xs block">Avg Balance</span><span className="font-medium text-emerald-700">{fmt$(data.average_balance)}</span></div>}
            {data.total_nsf != null && <div><span className="text-slate-400 text-xs block">NSF Count</span><span className={`font-medium ${data.total_nsf > 0 ? 'text-red-600' : 'text-emerald-600'}`}>{data.total_nsf}</span></div>}
          </div>
          {months.length > 0 && (
            <div className="overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr className="border-b">
                  <th className="text-left py-1 px-2">Month</th>
                  <th className="text-right py-1 px-2">Deposits</th>
                  <th className="text-right py-1 px-2">Withdrawals</th>
                  <th className="text-right py-1 px-2">Avg Balance</th>
                  <th className="text-right py-1 px-2">NSF</th>
                </tr></thead>
                <tbody>{months.map((m, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="py-1 px-2">{m.month_year}</td>
                    <td className="py-1 px-2 text-right">{fmt$(m.total_deposits)}</td>
                    <td className="py-1 px-2 text-right">{fmt$(m.total_withdrawals)}</td>
                    <td className="py-1 px-2 text-right">{fmt$(m.average_daily_balance)}</td>
                    <td className={`py-1 px-2 text-right ${m.nsf_count > 0 ? 'text-red-600 font-medium' : ''}`}>{m.nsf_count || 0}</td>
                  </tr>
                ))}</tbody>
              </table>
            </div>
          )}
        </div>
      );
    }

    if (toolType === 'pre_underwriting') {
      return (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
          {data.loan_type && <div><span className="text-slate-400 text-xs block">Loan Type</span><span className="font-medium">{data.loan_type}</span></div>}
          {data.annual_revenue != null && <div><span className="text-slate-400 text-xs block">Annual Revenue</span><span className="font-medium">{fmt$(data.annual_revenue)}</span></div>}
          {data.ebitda != null && <div><span className="text-slate-400 text-xs block">EBITDA</span><span className="font-medium">{fmt$(data.ebitda)}</span></div>}
          {data.fico_score != null && <div><span className="text-slate-400 text-xs block">FICO</span><span className="font-medium">{data.fico_score}</span></div>}
          {data.years_in_business != null && <div><span className="text-slate-400 text-xs block">Years in Business</span><span className="font-medium">{data.years_in_business}</span></div>}
          {data.state && <div><span className="text-slate-400 text-xs block">State</span><span className="font-medium">{data.state}</span></div>}
          {data.industry && <div><span className="text-slate-400 text-xs block">Industry</span><span className="font-medium">{data.industry}</span></div>}
          {data.business_debt != null && <div><span className="text-slate-400 text-xs block">Business Debt</span><span className="font-medium">{fmt$(data.business_debt)}</span></div>}
        </div>
      );
    }

    if (toolType === 'credit_analyzer') {
      return (
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
          {data.annualRevenue != null && <div><span className="text-slate-400 text-xs block">Annual Revenue</span><span className="font-medium">{fmt$(data.annualRevenue)}</span></div>}
          {data.ebitda != null && <div><span className="text-slate-400 text-xs block">EBITDA</span><span className="font-medium">{fmt$(data.ebitda)}</span></div>}
          {data.annualDebtService != null && <div><span className="text-slate-400 text-xs block">Annual Debt Service</span><span className="font-medium">{fmt$(data.annualDebtService)}</span></div>}
          {data.dscr != null && <div><span className="text-slate-400 text-xs block">DSCR</span><span className={`font-bold ${data.dscr >= 1.25 ? 'text-emerald-600' : data.dscr >= 1.0 ? 'text-amber-600' : 'text-red-600'}`}>{data.dscr?.toFixed(2)}x</span></div>}
        </div>
      );
    }

    // Generic fallback: display key-value pairs
    const entries = Object.entries(data).filter(([, v]) => v != null && v !== '' && !Array.isArray(v));
    return (
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 text-sm">
        {entries.map(([k, v]) => (
          <div key={k}><span className="text-slate-400 text-xs block">{k.replace(/_/g, ' ')}</span><span className="font-medium">{typeof v === 'number' ? (v > 999 ? fmt$(v) : v) : String(v)}</span></div>
        ))}
      </div>
    );
  };

  if (loading) return null;
  if (results.length === 0) return null;

  return (
    <Card data-testid="toolkit-results-section">
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2 flex-wrap">
          <CardTitle className="text-base flex items-center gap-2">
            <BarChart3 className="h-4 w-4 text-[#081120]" />
            Analysis & Reports
            <Badge variant="outline" className="text-xs">{results.length}</Badge>
          </CardTitle>
          {canRefreshGuidelines && results.length > 0 && (
            <button
              onClick={handleClearAll}
              className="inline-flex items-center gap-1.5 px-2.5 py-1 text-[11px] font-semibold rounded-md bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 hover:border-red-300 transition-colors"
              data-testid="clear-all-analyses-btn"
              title="Clear all saved analyses and reset to no-analysis state"
            >
              <Trash2 className="h-3.5 w-3.5" /> Clear All
            </button>
          )}
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        {results.map((result) => {
          const Icon = TOOL_TYPE_ICONS[result.tool_type] || FileText;
          const isExpanded = expandedId === result.id;
          return (
            <div key={result.id} className="border border-slate-200 rounded-lg overflow-hidden" data-testid={`toolkit-result-${result.id}`}>
              <div
                className="flex items-center justify-between px-4 py-3 bg-slate-50 cursor-pointer hover:bg-slate-100 transition-colors"
                onClick={() => setExpandedId(isExpanded ? null : result.id)}
              >
                <div className="flex items-center gap-2.5 min-w-0">
                  <Icon className="h-4 w-4 text-slate-500 flex-shrink-0" />
                  <span className="text-sm font-medium text-slate-700">{TOOL_TYPE_LABELS[result.tool_type] || result.tool_type}</span>
                  <span className="text-xs text-slate-400">by {result.saved_by_name}</span>
                  <span className="text-xs text-slate-400">{result.saved_at ? new Date(result.saved_at).toLocaleDateString() : ''}</span>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0">
                  {canRefreshGuidelines && (result.tool_type === 'tax_return_analysis' || result.tool_type === 'bank_statement_analysis') && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setGuidelineRefreshAt(prev => ({ ...prev, [result.id]: Date.now() }));
                        setRefreshedAt(prev => ({ ...prev, [result.id]: new Date() }));
                        // Auto-expand so the user sees the recomputed banner immediately
                        setExpandedId(result.id);
                      }}
                      className="p-1 rounded hover:bg-cyan-50 text-slate-400 hover:text-cyan-600 transition-colors"
                      title="Re-evaluate this analysis against the latest underwriting guidelines"
                      data-testid={`refresh-guidelines-${result.id}`}
                    >
                      <RefreshCw className="h-3.5 w-3.5" />
                    </button>
                  )}
                  <button
                    onClick={(e) => { e.stopPropagation(); handleDelete(result.id); }}
                    className="p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                    title="Remove"
                    data-testid={`delete-result-${result.id}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                  {isExpanded ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
                </div>
              </div>
              {isExpanded && (
                <div key={`${result.id}-${guidelineRefreshAt[result.id] || 0}`} className="px-4 py-3 border-t border-slate-200">
                  {refreshedAt[result.id] && (result.tool_type === 'tax_return_analysis' || result.tool_type === 'bank_statement_analysis') && (
                    <div className="mb-2 text-[11px] text-cyan-700 bg-cyan-50 border border-cyan-200 px-2 py-1 rounded inline-flex items-center gap-1" data-testid={`refreshed-banner-${result.id}`}>
                      <RefreshCw className="h-3 w-3" />
                      Recomputed against current guidelines · {refreshedAt[result.id].toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' })}
                    </div>
                  )}
                  {renderResultData(result.tool_type, result.result_data)}
                </div>
              )}
            </div>
          );
        })}
      </CardContent>
    </Card>
  );
};

const OfferDocumentsSection = ({ applicationId, token, existingFiles, onUpdate }) => {
  const [files, setFiles] = useState(existingFiles || []);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = React.useRef(null);
  const API = process.env.REACT_APP_BACKEND_URL;

  const handleUpload = async (e) => {
    const selected = e.target.files;
    if (!selected || selected.length === 0) return;
    setUploading(true);
    const fileList = Array.from(selected);
    let uploaded = [];
    for (const file of fileList) {
      try {
        const formData = new FormData();
        formData.append('file', file);
        const res = await axios.post(
          `${API}/api/applications/${applicationId}/offer-documents`,
          formData,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (res.data?.file) uploaded.push(res.data.file);
      } catch (err) {
        console.error(`Failed to upload ${file.name}:`, err);
      }
    }
    if (uploaded.length > 0) {
      setFiles(prev => [...prev, ...uploaded]);
    }
    setUploading(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleDelete = async (fileIndex) => {
    const file = files[fileIndex];
    if (!window.confirm(`Delete "${file.filename}"?`)) return;
    try {
      await axios.delete(
        `${API}/api/applications/${applicationId}/offer-documents/${fileIndex}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setFiles(prev => prev.filter((_, i) => i !== fileIndex));
    } catch (err) {
      console.error('Failed to delete:', err);
    }
  };

  return (
    <Card data-testid="offer-documents-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between">
          <span className="flex items-center gap-2">
            <FileText className="h-4 w-4 text-[#081120]" />
            Offer Documents
          </span>
          <div className="flex items-center gap-2">
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
              onChange={handleUpload}
            />
            <Button
              size="sm"
              variant="outline"
              onClick={() => { if (fileInputRef.current) { fileInputRef.current.value = ''; fileInputRef.current.click(); } }}
              disabled={uploading}
              data-testid="upload-offer-docs-btn"
            >
              {uploading
                ? <><Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" /> Uploading...</>
                : <><Upload className="h-3.5 w-3.5 mr-1" /> Upload Offer</>}
            </Button>
          </div>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {files.length === 0 ? (
          <p className="text-sm text-slate-400 text-center py-4">No offer documents uploaded yet. Upload term sheets, approval letters, or offer packages.</p>
        ) : (
          <div className="space-y-2">
            {files.map((file, idx) => (
              <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 border border-slate-200 rounded-lg" data-testid={`offer-doc-${idx}`}>
                <div className="flex items-center gap-2 min-w-0 flex-1">
                  <FileText className="h-4 w-4 text-slate-500 flex-shrink-0" />
                  <span className="text-sm font-medium text-slate-700 truncate">{file.filename}</span>
                  <span className="text-xs text-slate-400 flex-shrink-0">
                    {file.uploaded_at ? new Date(file.uploaded_at).toLocaleDateString() : ''}
                  </span>
                </div>
                <div className="flex items-center gap-1.5 flex-shrink-0 ml-2">
                  <a
                    href={`${API}/api/files/${applicationId}/${encodeURIComponent(file.stored_filename)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="p-1.5 rounded hover:bg-slate-200 text-slate-500 hover:text-slate-700 transition-colors"
                    title="Download"
                    data-testid={`download-offer-doc-${idx}`}
                  >
                    <ExternalLink className="h-3.5 w-3.5" />
                  </a>
                  <button
                    onClick={() => handleDelete(idx)}
                    className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                    title="Delete"
                    data-testid={`delete-offer-doc-${idx}`}
                  >
                    <Trash2 className="h-3.5 w-3.5" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const PROGRAM_OPTIONS = [
  { value: 'sba_loan', label: 'SBA Loan' },
  { value: 'term_loan_10yr', label: '10 Year Term Loan' },
  { value: 'term_loan_3_5yr', label: 'EZ Term Loan 3-5 Yr' },
  { value: 'term_loc_hybrid', label: 'Term Loan / LOC Hybrid' },
  { value: 'term_loan', label: 'Term Loan' },
  { value: 'line_of_credit', label: 'Line of Credit' },
  { value: 'equipment_financing', label: 'Equipment Financing' },
  { value: 'merchant_processing', label: 'Merchant Processing' },
  { value: 'jumbo_term_loan', label: 'Jumbo Term Loan' },
  { value: 'transportation_factoring', label: 'Transportation Factoring' },
  { value: 'real_estate_financing', label: 'Real Estate Financing' },
  { value: 'debt_consulting', label: 'Debt Consolidation' },
  { value: 'foreclosure_bailout', label: 'Foreclosure Bailout' },
  { value: 'family_office', label: 'Family Office Programs' },
];

const FAMILY_OFFICE_SUBTYPE_OPTIONS = [
  { value: 'term_loans',                label: 'Term Loans' },
  { value: 'lines_of_credit',           label: 'Lines of Credit' },
  { value: 'non_collateralized_loans',  label: 'Non-Collateralized Loans' },
  { value: 'asset_based_lending',       label: 'Asset-Based Lending' },
  { value: 'equipment_financing',       label: 'Equipment Financing' },
];

const ConvertProgramSection = ({ application, token, onConvert }) => {
  const [showConvert, setShowConvert] = useState(false);
  const [targetType, setTargetType] = useState('');
  const [selectedSubtypes, setSelectedSubtypes] = useState([]);
  const [converting, setConverting] = useState(false);
  const [result, setResult] = useState(null);

  const currentType = application.product_type;
  const available = PROGRAM_OPTIONS.filter(p => p.value !== currentType);
  const currentLabel = PROGRAM_OPTIONS.find(p => p.value === currentType)?.label || currentType;
  const needsSubtypes = targetType === 'family_office';

  const toggleSubtype = (id) => {
    setSelectedSubtypes(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleConvert = async () => {
    if (!targetType) return;
    if (needsSubtypes && selectedSubtypes.length === 0) {
      toast.error('Please select at least one sub-program for Family Office.');
      return;
    }
    const targetLabel = PROGRAM_OPTIONS.find(p => p.value === targetType)?.label || targetType;
    if (!window.confirm(`Convert this application from "${currentLabel}" to "${targetLabel}"?\n\nMatching fields will be mapped automatically. Fields that don't map will be preserved and accessible by the AI assistant.`)) return;

    setConverting(true);
    setResult(null);
    try {
      const payload = { target_type: targetType };
      if (needsSubtypes) payload.selected_subtypes = selectedSubtypes;
      const { data } = await axios.put(
        `${process.env.REACT_APP_BACKEND_URL}/api/applications/${application.id}/convert`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(data);
      toast.success(`Converted to ${targetLabel}: ${data.mapped_fields} fields mapped`);
      setShowConvert(false);
      setTargetType('');
      setSelectedSubtypes([]);
      if (onConvert) onConvert();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Conversion failed');
    } finally {
      setConverting(false);
    }
  };

  return (
    <Card className="border-slate-200" data-testid="convert-program-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Repeat2 className="h-4 w-4 text-[#081120]" />
            Convert Program Type
          </span>
          <Badge variant="outline" className="text-xs">{currentLabel}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {application?.conversion_preserved_data && Object.keys(application.conversion_preserved_data).length > 0 && (
          <div className="mb-3 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="remap-conversion-banner">
            <div className="flex items-start justify-between gap-3">
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-900">Previously converted from {Object.keys(application.conversion_preserved_data)[0]?.replace(/_/g, ' ')}</p>
                <p className="text-xs text-amber-800 mt-0.5">Missing fields? Re-run the mapping against the original data using the latest field-equivalence rules. Only empty fields will be filled — nothing you've already typed will be overwritten.</p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="border-amber-300 text-amber-900 hover:bg-amber-100 whitespace-nowrap"
                onClick={async () => {
                  try {
                    const { data } = await axios.post(
                      `${process.env.REACT_APP_BACKEND_URL}/api/applications/${application.id}/remap-conversion`,
                      {},
                      { headers: { Authorization: `Bearer ${token}` } }
                    );
                    toast.success(data.message || `Backfilled ${data.filled} field(s)`);
                    if (onConvert) onConvert();
                  } catch (err) {
                    toast.error(err.response?.data?.detail || 'Remap failed');
                  }
                }}
                data-testid="remap-conversion-btn"
              >
                Backfill Missing Fields
              </Button>
            </div>
          </div>
        )}
        {!showConvert ? (
          <Button
            variant="outline"
            size="sm"
            className="text-sm"
            onClick={() => setShowConvert(true)}
            data-testid="convert-program-toggle"
          >
            <Repeat2 className="h-3.5 w-3.5 mr-1.5" />
            Convert to Different Program
          </Button>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Convert this application to a different loan program. Matching data fields will be automatically mapped. Unmapped fields will be preserved.
            </p>
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <Label className="text-xs text-slate-500 mb-1 block">Target Program</Label>
                <select
                  value={targetType}
                  onChange={(e) => { setTargetType(e.target.value); setSelectedSubtypes([]); }}
                  className="w-full h-9 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                  data-testid="convert-target-select"
                >
                  <option value="">Select program...</option>
                  {available.map(p => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>
              <Button
                size="sm"
                className="bg-[#081120] hover:bg-[#0f1d33] text-white"
                disabled={!targetType || converting || (needsSubtypes && selectedSubtypes.length === 0)}
                onClick={handleConvert}
                data-testid="convert-confirm-btn"
              >
                {converting ? 'Converting...' : 'Convert'}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => { setShowConvert(false); setTargetType(''); setSelectedSubtypes([]); }}
              >
                Cancel
              </Button>
            </div>
            {needsSubtypes && (
              <div className="mt-3 p-3 bg-violet-50 border border-violet-200 rounded-lg space-y-2" data-testid="convert-subtype-picker">
                <p className="text-sm font-medium text-violet-900">Which Family Office sub-programs apply?</p>
                <p className="text-xs text-violet-700">Required documents are set based on your selection. You can pick more than one.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 pt-1">
                  {FAMILY_OFFICE_SUBTYPE_OPTIONS.map(st => (
                    <label key={st.value} className="flex items-center gap-2 text-sm cursor-pointer p-1.5 rounded hover:bg-violet-100">
                      <input
                        type="checkbox"
                        checked={selectedSubtypes.includes(st.value)}
                        onChange={() => toggleSubtype(st.value)}
                        data-testid={`convert-subtype-${st.value}`}
                      />
                      {st.label}
                    </label>
                  ))}
                </div>
                {selectedSubtypes.length === 0 && (
                  <p className="text-xs text-red-600 pt-1">Select at least one sub-program to continue.</p>
                )}
              </div>
            )}
            {result && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm">
                <p className="text-emerald-800 font-medium">{result.message}</p>
                <p className="text-emerald-600 text-xs mt-1">
                  {result.mapped_fields} fields mapped{result.unmapped_fields > 0 ? `, ${result.unmapped_fields} fields preserved` : ''}
                </p>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const DedupeDocumentFilesSection = ({ application, token, onFixed }) => {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  // Count files that appear in more than one slot (client-side preview).
  const { duplicateCount, uniqueFileCount } = useMemo(() => {
    const seen = new Map();
    const bump = (sf) => {
      if (!sf) return;
      seen.set(sf, (seen.get(sf) || 0) + 1);
    };
    (application.required_documents || []).forEach(d => {
      (d.files || []).forEach(f => bump(f.stored_filename));
      if (d.stored_filename && !(d.files || []).some(f => f.stored_filename === d.stored_filename)) bump(d.stored_filename);
    });
    (application.additional_documents || []).forEach(d => (d.files || []).forEach(f => bump(f.stored_filename)));
    let dupes = 0;
    seen.forEach(count => { if (count > 1) dupes += (count - 1); });
    return { duplicateCount: dupes, uniqueFileCount: seen.size };
  }, [application.required_documents, application.additional_documents]);

  if (duplicateCount === 0) return null;

  const handleDedupe = async () => {
    if (!window.confirm(
      `Remove ${duplicateCount} duplicate file entry${duplicateCount === 1 ? '' : 'ies'}?\n\n` +
      `Files that appear in BOTH a required-doc slot AND Additional Documents will be trimmed to a single copy (kept where it was originally uploaded).\n\n` +
      `No files are deleted from storage — just the extra reference entries. This is safe to run repeatedly.`
    )) return;
    setRunning(true);
    setResult(null);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/applications/${application.id}/dedupe-document-files`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(data);
      if (data.duplicates_removed > 0) {
        toast.success(`Removed ${data.duplicates_removed} duplicate file entr${data.duplicates_removed === 1 ? 'y' : 'ies'}.`);
      } else {
        toast.info('No duplicates found — everything is already clean.');
      }
      onFixed && onFixed();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Dedupe failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card className="border-red-200 bg-red-50/40" data-testid="dedupe-docs-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <AlertCircle className="h-4 w-4 text-red-700" />
          Duplicate File Entries Detected
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-slate-700 mb-3">
          <strong>{duplicateCount}</strong> duplicate file entr{duplicateCount === 1 ? 'y' : 'ies'} found across
          this application&apos;s document slots (out of {uniqueFileCount} unique files). This usually happens when the
          same file has been re-attached by an older automatic recovery task. Clean these up before deleting
          anything — otherwise deleting the duplicate can break the original.
        </p>
        <Button
          size="sm"
          variant="outline"
          onClick={handleDedupe}
          disabled={running}
          className="border-red-300 hover:bg-red-100 text-red-800"
          data-testid="dedupe-docs-btn"
        >
          {running
            ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Cleaning up…</>
            : <><Wrench className="h-3.5 w-3.5 mr-1.5" /> Remove duplicate entries</>}
        </Button>
        {result && (
          <p className="text-xs text-emerald-800 mt-2" data-testid="dedupe-result">
            Removed {result.duplicates_removed} · Kept {result.files_after} unique file{result.files_after === 1 ? '' : 's'}.
          </p>
        )}
      </CardContent>
    </Card>
  );
};

const RescueDocSlotsSection = ({ application, token, onRescued }) => {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);

  const additionalCount = (application.additional_documents || []).reduce(
    (n, d) => n + (d.files || []).length, 0
  );
  if (additionalCount === 0) return null;

  const handleRescue = async () => {
    if (!window.confirm(`Re-route files from "Additional Documents" back into the matching required-doc slots? This will look for items like "Last 6 months bank statements" and move them into "6 Months Bank Statements" automatically.`)) return;
    setRunning(true);
    setResult(null);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/applications/${application.id}/rescue-doc-slots`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(data);
      if (data.moved > 0) {
        toast.success(`Re-routed ${data.moved} file(s) into their proper required-doc slots.`);
      } else {
        toast.info('Nothing to rescue — all additional documents are already in unique categories.');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Rescue failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card className="border-amber-200 bg-amber-50/30" data-testid="rescue-doc-slots-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center gap-2">
          <Wrench className="h-4 w-4 text-amber-700" />
          Rescue Misplaced Files
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-xs text-slate-600 mb-3">
          {additionalCount} file{additionalCount === 1 ? '' : 's'} are currently sitting under
          {' '}<strong>Additional Documents</strong>. If this application was converted from another
          program before the slot-matching fix, your bank statements / tax returns / etc. may have
          ended up here instead of in their required slots. Click below to auto-route them.
        </p>
        <Button
          size="sm"
          variant="outline"
          onClick={handleRescue}
          disabled={running}
          className="border-amber-300 hover:bg-amber-100"
          data-testid="rescue-doc-slots-btn"
        >
          {running ? <><Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> Rescuing...</> : <><Wrench className="h-3.5 w-3.5 mr-1.5" /> Auto-route to required slots</>}
        </Button>
        {result && (
          <p className="text-xs text-emerald-800 mt-2" data-testid="rescue-result">
            {result.message} ({result.remaining_in_additional} file{result.remaining_in_additional === 1 ? '' : 's'} still in additional.)
          </p>
        )}
      </CardContent>
    </Card>
  );
};

const DuplicateProgramSection = ({ application, token, onDuplicated }) => {
  const [showPanel, setShowPanel] = useState(false);
  const [targetType, setTargetType] = useState('');
  const [selectedSubtypes, setSelectedSubtypes] = useState([]);
  const [duplicating, setDuplicating] = useState(false);
  const [result, setResult] = useState(null);

  const currentLabel = PROGRAM_OPTIONS.find(p => p.value === application.product_type)?.label || application.product_type;
  const needsSubtypes = targetType === 'family_office';

  const toggleSubtype = (id) => {
    setSelectedSubtypes(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleDuplicate = async () => {
    if (!targetType) return;
    if (needsSubtypes && selectedSubtypes.length === 0) {
      toast.error('Please select at least one sub-program for Family Office.');
      return;
    }
    const targetLabel = PROGRAM_OPTIONS.find(p => p.value === targetType)?.label || targetType;
    if (!window.confirm(`Create a NEW "${targetLabel}" application from this one?\n\nThe original "${currentLabel}" application will be preserved untouched. The new application will share the same applicant/business but start fresh with the new program's required documents.`)) return;

    setDuplicating(true);
    setResult(null);
    try {
      const payload = { target_type: targetType };
      if (needsSubtypes) payload.selected_subtypes = selectedSubtypes;
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/applications/${application.id}/duplicate-as`,
        payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(data);
      const filesNote = (data.files_copied || 0) > 0
        ? ` ${data.files_copied} file${data.files_copied === 1 ? '' : 's'} copied (${data.files_mapped_to_required} mapped to required slots, ${data.files_kept_as_additional} kept as additional documents).`
        : '';
      const failNote = (data.files_failed || 0) > 0
        ? ` ⚠ ${data.files_failed} source file${data.files_failed === 1 ? '' : 's'} could not be copied — see server logs for details.`
        : '';
      if ((data.files_attempted || 0) > 0 && (data.files_copied || 0) === 0) {
        toast.warning(`New ${targetLabel} application created — ${data.mapped_fields} fields carried over, but NONE of the ${data.files_attempted} source files were copied.${failNote}`);
      } else {
        toast.success(`New ${targetLabel} application created — ${data.mapped_fields} fields carried over.${filesNote}${failNote}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Duplication failed');
    } finally {
      setDuplicating(false);
    }
  };

  const handleOpenNew = () => {
    if (result?.new_application_id && onDuplicated) {
      onDuplicated();
    }
  };

  const available = PROGRAM_OPTIONS;

  return (
    <Card className="border-slate-200" data-testid="duplicate-program-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between">
          <span className="flex items-center gap-2">
            <Plus className="h-4 w-4 text-[#081120]" />
            Create Another Application
          </span>
          <Badge variant="outline" className="text-xs">From: {currentLabel}</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {!showPanel ? (
          <Button
            variant="outline"
            size="sm"
            className="text-sm"
            onClick={() => setShowPanel(true)}
            data-testid="duplicate-program-toggle"
          >
            <Plus className="h-3.5 w-3.5 mr-1.5" />
            Create Another Application
          </Button>
        ) : (
          <div className="space-y-3">
            <p className="text-sm text-slate-600">
              Create a brand-new application of a different (or same) program type using this application&apos;s data as a starting point.
              The current application stays exactly as it is — this only spawns an additional sibling application.
            </p>
            <div className="flex gap-2 items-end">
              <div className="flex-1">
                <Label className="text-xs text-slate-500 mb-1 block">New Application Program</Label>
                <select
                  value={targetType}
                  onChange={(e) => { setTargetType(e.target.value); setSelectedSubtypes([]); }}
                  className="w-full h-9 px-3 rounded-md border border-slate-200 bg-white text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                  data-testid="duplicate-target-select"
                >
                  <option value="">Select program...</option>
                  {available.map(p => (
                    <option key={p.value} value={p.value}>{p.label}</option>
                  ))}
                </select>
              </div>
              <Button
                size="sm"
                className="bg-[#081120] hover:bg-[#0f1d33] text-white"
                disabled={!targetType || duplicating || (needsSubtypes && selectedSubtypes.length === 0)}
                onClick={handleDuplicate}
                data-testid="duplicate-confirm-btn"
              >
                {duplicating ? 'Creating...' : 'Create New'}
              </Button>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => { setShowPanel(false); setTargetType(''); setSelectedSubtypes([]); setResult(null); }}
                data-testid="duplicate-cancel-btn"
              >
                Cancel
              </Button>
            </div>
            {needsSubtypes && (
              <div className="mt-3 p-3 bg-violet-50 border border-violet-200 rounded-lg space-y-2" data-testid="duplicate-subtype-picker">
                <p className="text-sm font-medium text-violet-900">Which Family Office sub-programs apply?</p>
                <p className="text-xs text-violet-700">Required documents on the new application are set based on your selection. Pick more than one if needed.</p>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5 pt-1">
                  {FAMILY_OFFICE_SUBTYPE_OPTIONS.map(st => (
                    <label key={st.value} className="flex items-center gap-2 text-sm cursor-pointer p-1.5 rounded hover:bg-violet-100">
                      <input
                        type="checkbox"
                        checked={selectedSubtypes.includes(st.value)}
                        onChange={() => toggleSubtype(st.value)}
                        data-testid={`duplicate-subtype-${st.value}`}
                      />
                      {st.label}
                    </label>
                  ))}
                </div>
              </div>
            )}
            {result && (
              <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-sm" data-testid="duplicate-success-banner">
                <p className="text-emerald-800 font-medium">{result.message}</p>
                <p className="text-emerald-600 text-xs mt-1">
                  New application created with {result.mapped_fields} fields carried over.
                  {(result.files_copied || 0) > 0 && (
                    <> {' '}{result.files_copied} document{result.files_copied === 1 ? '' : 's'} copied
                      ({result.files_mapped_to_required} mapped to required-doc slots, {result.files_kept_as_additional} kept as additional documents).</>
                  )}
                  {' '}Both applications are now grouped together on the dashboard.
                </p>
                <div className="mt-2 flex gap-2">
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-xs h-7"
                    onClick={handleOpenNew}
                    data-testid="duplicate-open-new-btn"
                  >
                    <ExternalLink className="h-3 w-3 mr-1" />
                    Close & View Dashboard
                  </Button>
                </div>
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const CopyFilesFromSourceSection = ({ application, token, onCopied }) => {
  const [running, setRunning] = useState(false);
  const [result, setResult] = useState(null);
  const hasFilesCount = (() => {
    let n = 0;
    for (const s of (application?.required_documents || [])) n += (s?.files?.length || 0);
    for (const s of (application?.additional_documents || [])) n += (s?.files?.length || 0);
    return n;
  })();

  const run = async () => {
    if (!window.confirm('Copy uploaded documents from the source application into this one?\n\nSafe to re-run — already-copied files are skipped.')) return;
    setRunning(true);
    setResult(null);
    try {
      const { data } = await axios.post(
        `${process.env.REACT_APP_BACKEND_URL}/api/applications/${application.id}/copy-files-from-source`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setResult(data);
      if ((data.files_copied || 0) === 0 && (data.files_failed || 0) === 0) {
        toast.info('Nothing new to copy — all source files were already present here.');
      } else {
        const failNote = (data.files_failed || 0) > 0 ? ` ⚠ ${data.files_failed} could not be located on the source.` : '';
        toast.success(`Copied ${data.files_copied} file${data.files_copied === 1 ? '' : 's'} (${data.files_mapped_to_required} to required slots, ${data.files_kept_as_additional} to additional).${failNote}`);
      }
      if (onCopied) onCopied();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Copy failed');
    } finally {
      setRunning(false);
    }
  };

  return (
    <Card className="border-slate-200" data-testid="copy-files-from-source-section">
      <CardHeader className="pb-3">
        <CardTitle className="text-base flex items-center justify-between">
          <span className="flex items-center gap-2">
            <ExternalLink className="h-4 w-4 text-[#081120]" />
            Copy Documents From Source
          </span>
          <Badge variant="outline" className="text-xs">{hasFilesCount} file{hasFilesCount === 1 ? '' : 's'} here</Badge>
        </CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-slate-600 mb-3">
          This application was created via <strong>Create Another Application</strong>. If the source app has uploaded documents that didn&apos;t come over, click below to pull them in now. Already-copied files are skipped so it&apos;s safe to re-run.
        </p>
        <Button
          variant="outline"
          size="sm"
          onClick={run}
          disabled={running}
          data-testid="copy-files-from-source-btn"
        >
          {running ? 'Copying...' : 'Copy Files From Source'}
        </Button>
        {result && (
          <div className="mt-3 p-3 bg-slate-50 border border-slate-200 rounded text-xs text-slate-700 space-y-1" data-testid="copy-files-result">
            <div><strong>Copied:</strong> {result.files_copied}  (required: {result.files_mapped_to_required}, additional: {result.files_kept_as_additional})</div>
            {(result.files_failed || 0) > 0 && (
              <div className="text-amber-800">
                <strong>Failed:</strong> {result.files_failed} — {(result.clone_failures || []).slice(0, 5).map(f => f.filename || f.stored_filename).join(', ')}
                {(result.clone_failures || []).length > 5 && ' …'}
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};


export default ApplicationReview;
